import React, { useState, useEffect, useCallback } from 'react';
import {
  mediaVariantIds,
  isVariantOnMedia,
  mediaForVariantView,
  findMediaForVariant,
  assignVariantToMedia,
  mediaPayloadItem,
  otherVariantNames,
} from '../../utils/mediaUtils';
import { getProductAuditHistory, listCategories, getProductDetail, getProductReviewSummary, updateProduct, updateProductOptions, getCurrentUserRole, assignVariantImage } from '../../services/shipturtleApi';
import { exportProductLogs } from '../../api/api';
import { hasBlobUrls } from '../../utils/helpers';
import { normalizeVariantOptions, buildOptionValuesFromCombo, getOptionValue } from '../../utils/optionUtils';
import HTMLEditor from './HTMLEditor';
import { StatusDropdown } from './ProductDetailView';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ChangesReviewPanel from './ChangesReviewPanel';
import './ManageProducts.css';

// Workflow transitions that shuttle a product between the manage and approval
// queues. They aren't vendor content edits, so they're hidden from the changelog.
const NON_CONTENT_CHANGE_FIELDS = new Set(['status', 'state', 'terminal_status']);

// Audit payloads spell "nothing here" several ways (null, '', [], {}); collapse
// them so a no-op isn't rendered as a change.
const normalizeChangeValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) return value.length === 0 ? '' : JSON.stringify(value);
  if (typeof value === 'object') {
    return Object.keys(value).length === 0 ? '' : JSON.stringify(value);
  }
  return value;
};

const isMeaningfulChange = (oldValue, newValue) =>
  normalizeChangeValue(oldValue) !== normalizeChangeValue(newValue);

const SortableImage = ({ image, index, isReadOnly, assignedVariant, onRemove, isSelected, onToggleSelect }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: image.id || image.url });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 100 : 1,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    cursor: isReadOnly ? 'default' : 'grab'
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} className={`imageItem ${index === 0 ? 'primary-image' : ''}`}>
      <div className="imageWrapper">
        <div className="imageCheckbox" onPointerDown={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect(image.id || image.url)}
          />
        </div>
        <img src={image.url} alt={image.alt_text || `Product ${index + 1}`} />
        {image.is_primary && <span className="primaryBadge">Primary</span>}
      </div>
    </div>
  );
};

const ImagePickerModal = ({ 
  showImagePickerModal, 
  setShowImagePickerModal, 
  imagePickerVariant, 
  allMediaItems, 
  allVariants, 
  handleVariantImageChange 
}) => {
  if (!showImagePickerModal) return null;

  const currentVariantId = imagePickerVariant?.id || imagePickerVariant?.tempId;
  const currentImageId = imagePickerVariant?.image_id
    || findMediaForVariant(allMediaItems, currentVariantId)?.id
    || null;

  const pickerItems = allMediaItems.reduce((acc, m) => {
    const existingIdx = acc.findIndex(x => x.url === m.url);
    if (existingIdx === -1) {
      acc.push(m);
    } else if (m.id === currentImageId) {
      acc[existingIdx] = m;
    }
    return acc;
  }, []);

  return (
    <div className="imagePickerModal">
      <div className="imagePickerContent">
        <div className="imagePickerHeader">
          <h3>Assign Image to Variant: {imagePickerVariant?.variant_name}</h3>
          <button type="button" onClick={() => setShowImagePickerModal(false)} className="closeButton">×</button>
        </div>
        <div className="imagePickerBody">
          <div className="imagePickerGrid">
            {pickerItems.map((item, idx) => (
              <div
                key={item.id || idx}
                className={`imagePickerItem ${currentImageId === item.id ? 'selected' : ''}`}
                onClick={() => handleVariantImageChange(currentVariantId, item.id)}
              >
                <img src={item.url} alt={item.alt} />
                {item.is_primary && <span className="primaryIndicator">Primary</span>}
                {(() => {
                  // An image can serve several variants — name the others, or count
                  // them. The scalar could only ever name one.
                  const names = otherVariantNames(item, allVariants, currentVariantId);
                  if (!names.length) return null;
                  return (
                    <span className="imageBadge" title={names.join(', ')}>
                      {names.length === 1 ? names[0] : `${names.length} variants`}
                    </span>
                  );
                })()}
              </div>
            ))}
          </div>
        </div>
        <div className="imagePickerFooter">
          {currentImageId && (
            <button
              type="button"
              className="bulkActionButton danger"
              onClick={() => {
                handleVariantImageChange(currentVariantId, null);
                setShowImagePickerModal(false);
              }}
            >
              Remove Image
            </button>
          )}
          <button
            type="button"
            className="bulkActionButton"
            onClick={() => setShowImagePickerModal(false)}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const InlineCell = ({ value, variantId, field, onSave, isSaving, error, type = 'text', setHasChanges }) => {
  const [editing, setEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleBlur = () => {
    setEditing(false);
    if (String(localValue) !== String(value)) {
      onSave(variantId, field, localValue);
      if (setHasChanges) setHasChanges(true);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
    if (e.key === 'Escape') {
      setLocalValue(value);
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <input
        type={type}
        className={`variantsCompactInput ${error ? 'error' : ''}`}
        value={localValue || ''}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        autoFocus
      />
    );
  }

  return (
    <div 
      className={`variantsReadOnlyName ${isSaving ? 'saving' : ''} ${error ? 'error' : ''}`}
      onClick={() => setEditing(true)}
      title="Click to edit"
    >
      {isSaving ? '...' : (value !== null && value !== undefined && value !== '' ? value : '-')}
    </div>
  );
};

const VariantInlineEditForm = ({ variant, onCancel, onSave, onUpdate, setImagePickerVariant, setShowImagePickerModal }) => {
  const [draft, setDraft] = useState({
    ...variant,
    variant_name: variant.variant_name || variant.name || '',
    inventory: variant.meta?.inventory || 0,
  });
  const [hsCodeError, setHsCodeError] = useState('');

  const handleChange = (field, value) => {
    const updated = { ...draft, [field]: value };
    setDraft(updated);
    onUpdate?.(updated);
    if (field === 'hs_code') {
      if (value && value !== '' && (!/^\d{4,8}$/.test(value))) {
        setHsCodeError('HSN code must be 4 to 8 digits (numbers only)');
      } else {
        setHsCodeError('');
      }
    }
  };

  const handleSave = () => {
    if (draft.hs_code && draft.hs_code !== '' && (!/^\d{4,8}$/.test(draft.hs_code))) {
      setHsCodeError('HSN code must be 4 to 8 digits (numbers only)');
      return;
    }
    onSave(draft);
  };

  return (
    <div className="variantEditForm">
      {setImagePickerVariant && setShowImagePickerModal && (
        <div className="formGroup" style={{ gridColumn: '1 / -1' }}>
          <label>Variant image</label>
          <button
            type="button"
            className="bulkActionButton"
            onClick={() => {
              setImagePickerVariant(variant);
              setShowImagePickerModal(true);
            }}
          >
            Select image
          </button>
        </div>
      )}
      <div className="formGroup"><label>Variant name</label><input className="formInput" value={draft.variant_name || ''} onChange={(e) => handleChange('variant_name', e.target.value)} /></div>
      <div className="formGroup"><label>SKU</label><input className="formInput" value={draft.sku || ''} onChange={(e) => handleChange('sku', e.target.value.toUpperCase())} /></div>
      <div className="formGroup"><label>Price</label><input className="formInput" type="number" value={draft.price || ''} onChange={(e) => handleChange('price', e.target.value)} /></div>
      <div className="formGroup"><label>Inventory</label><input className="formInput" type="number" min="0" value={draft.inventory || 0} onChange={(e) => handleChange('inventory', e.target.value)} /></div>
      <div className="formGroup"><label>Compare at price</label><input className="formInput" type="number" value={draft.compare_price || ''} onChange={(e) => handleChange('compare_price', e.target.value)} /></div>
      <div className="formGroup">
        <label>HSN code</label>
        <input
          className={`formInput ${hsCodeError ? 'error' : ''}`}
          value={draft.hs_code || ''}
          onChange={(e) => handleChange('hs_code', e.target.value)}
        />
        {hsCodeError && <span className="fieldError">{hsCodeError}</span>}
      </div>
      <div className="formGroup"><label>GST (%)</label><input className="formInput" type="number" value={draft.gst_rate ?? ''} onChange={(e) => handleChange('gst_rate', e.target.value)} /></div>
      <div className="formGroup"><label>Weight (kg)</label><input className="formInput" type="number" value={draft.weight_kg || ''} onChange={(e) => handleChange('weight_kg', e.target.value)} /></div>
      <div className="formGroup"><label>Length (cm)</label><input className="formInput" type="number" value={draft.length_cm || ''} onChange={(e) => handleChange('length_cm', e.target.value)} /></div>
      <div className="formGroup"><label>Width (cm)</label><input className="formInput" type="number" value={draft.width_cm || ''} onChange={(e) => handleChange('width_cm', e.target.value)} /></div>
      <div className="formGroup"><label>Height (cm)</label><input className="formInput" type="number" value={draft.height_cm || ''} onChange={(e) => handleChange('height_cm', e.target.value)} /></div>

      <div className="variantEditActions">
        <button type="button" className="bulkActionButton" onClick={onCancel}>Cancel</button>
        <button type="button" className="bulkActionButton primary" onClick={handleSave}>Save</button>
      </div>
    </div>
  );
};

const VariantTable = ({
  allVariants,
  options,
  selectedVariantRowIndexes,
  allMediaItems,
  variantGroupBy,
  expandedVariantGroups,
  validationErrors,
  savingVariantField,
  handleVariantFieldChange,
  toggleProductVariantRowSelection,
  handleProductVariantsHeaderSelectionClick,
  setVariantGroupBy,
  setExpandedVariantGroups,
  setHasChanges,
  expandedVariantId,
  setExpandedVariantId,
  setAllVariants,
  setImagePickerVariant,
  setShowImagePickerModal,
  onDeleteVariant
}) => {
  const variants = (allVariants || []).filter(v => !v._delete);
  const optionNames = (options || []).map((option) => option.name).filter(Boolean);

  const totalVariants = variants.length;
  let variantsSelectedCount = 0;
  for (let i = 0; i < totalVariants; i++) {
    if (selectedVariantRowIndexes.has(i)) variantsSelectedCount++;
  }
  const variantsAllSelected =
    totalVariants > 0 && variantsSelectedCount === totalVariants;
  const variantsPartiallySelected =
    variantsSelectedCount > 0 && variantsSelectedCount < totalVariants;

  const calculateTotalInventory = () => {
    return variants.reduce((sum, v) => sum + (parseInt(v.meta?.inventory) || 0), 0);
  };

  // Returns { url, isAssigned }. isAssigned=false means the url (if any) is
  // just a product-level image shown as a preview placeholder — it is NOT
  // actually bound to this variant in the backend. Callers MUST render this
  // differently from a real assignment (see variantImgUnassigned CSS).
  const getVariantImage = (variant) => {
    if (variant.image_id) {
      const byId = allMediaItems.find(m => m.id === variant.image_id);
      if (byId?.url) return { url: byId.url, isAssigned: true };
    }
    const variantId = variant.id || variant.tempId;
    const byVariantId = findMediaForVariant(allMediaItems, variantId);
    if (byVariantId?.url) return { url: byVariantId.url, isAssigned: true };
    // Fallback: match by the URL stored directly on the variant (older products without variant_id linkage)
    const assignedUrl = (variant.image_urls || '').split('|')[0].trim() || variant.image_url || null;
    if (assignedUrl) {
      const byUrl = allMediaItems.find(m => m.url === assignedUrl);
      if (byUrl?.url) return { url: byUrl.url, isAssigned: true };
    }
    const firstProductImage = allMediaItems.find(m => mediaVariantIds(m).length === 0);
    return { url: firstProductImage ? firstProductImage.url : null, isAssigned: false };
  };

  const handleToggleExpand = (v) => {
    const vid = v.id || v.tempId;
    if (expandedVariantId === vid) {
      setExpandedVariantId(null);
    } else {
      setExpandedVariantId(vid);
    }
  };

  const handleImageClick = (v) => {
    setImagePickerVariant(v);
    setShowImagePickerModal(true);
  };

  return (
    <div className="variantTableContainer">
      {optionNames.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0.75rem', borderBottom: '1px solid #f0f0f0' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem' }}>
            Group by
            <select
              className="formInput"
              style={{ width: '180px' }}
              value={variantGroupBy || optionNames[0]}
              onChange={(e) => setVariantGroupBy(e.target.value)}
            >
              {optionNames.map((optionName) => (
                <option key={optionName} value={optionName}>{optionName}</option>
              ))}
            </select>
          </label>
        </div>
      )}
      <div className="tableWrapper">
        <table className="variantTable">
          <thead>
            <tr>
              <th className="checkboxColumn" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  className="variantsCompactHeaderTriCheckbox"
                  role="checkbox"
                  aria-checked={variantsPartiallySelected ? 'mixed' : variantsAllSelected}
                  aria-label={
                    variantsAllSelected ? 'Deselect all variants' : 'Select all variants'
                  }
                  disabled={totalVariants === 0}
                  onClick={handleProductVariantsHeaderSelectionClick}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleProductVariantsHeaderSelectionClick(e);
                    }
                  }}
                >
                  <span className="variantsCompactHeaderTriCheckbox-box" aria-hidden>
                    {variantsAllSelected ? (
                      <span className="variantsCompactHeaderTriCheckbox-check">✓</span>
                    ) : variantsPartiallySelected ? (
                      <span className="variantsCompactHeaderTriCheckbox-dash">−</span>
                    ) : null}
                  </span>
                </button>
              </th>
              <th>Image</th>
              <th>Variant</th>
              <th>SKU</th>
              <th>Price</th>
              <th>Inventory</th>
              <th style={{ width: '50px' }}></th>
            </tr>
          </thead>
          <tbody>
            {variants.map((v, idx) => {
              const groupName = variantGroupBy || optionNames[0];
              const groupValue = groupName ? (getOptionValue(v, groupName) || 'Other') : null;
              const isFirstInGroup = groupValue
                ? variants.findIndex((item) => ((getOptionValue(item, groupName) || 'Other') === groupValue)) === idx
                : false;
              const isExpandedGroup = groupValue ? expandedVariantGroups[groupValue] !== false : true;
              if (groupValue && !isExpandedGroup && !isFirstInGroup) {
                return null;
              }

              const variantId = v.id || v.tempId;
              const isVariantExpanded = expandedVariantId === variantId;

              return (
              <React.Fragment key={v.id || v.tempId || idx}>
                {isFirstInGroup && (
                  <tr>
                    <td
                      colSpan={7}
                      style={{ background: '#f7f7f7', fontWeight: 600, cursor: 'pointer' }}
                      onClick={() => setExpandedVariantGroups((prev) => ({ ...prev, [groupValue]: prev[groupValue] === false }))}
                    >
                      {groupValue} ({variants.filter((item) => ((getOptionValue(item, groupName) || 'Other') === groupValue)).length} variants)
                    </td>
                  </tr>
                )}
              <tr className={isVariantExpanded ? 'variantActiveRow' : ''}>
                <td className="checkboxColumn" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedVariantRowIndexes.has(idx)}
                    onChange={() => toggleProductVariantRowSelection(idx)}
                  />
                </td>
                <td onClick={() => handleImageClick(v)} style={{ cursor: 'pointer' }}>
                  {(() => {
                    const { url: variantImageUrl, isAssigned } = getVariantImage(v);
                    if (variantImageUrl && isAssigned) {
                      return <img src={variantImageUrl} alt={v.variant_name} className="variantImg" />;
                    }
                    if (variantImageUrl && !isAssigned) {
                      return (
                        <div
                          className="variantImgUnassigned"
                          title="No image assigned to this variant yet — click to assign"
                        >
                          <img src={variantImageUrl} alt="" className="variantImgUnassignedPreview" />
                          <span className="variantImgUnassignedBadge">+</span>
                        </div>
                      );
                    }
                    return <div className="variantImgPlaceholder">+</div>;
                  })()}
                </td>
                <td>
                  <span className="variantsReadOnlyName">
                    {v.variant_name || v.name || '-'}
                  </span>
                </td>
                <td>
                  <InlineCell
                    value={v.sku}
                    variantId={v.id || v.tempId}
                    field="sku"
                    onSave={(vid, f, val) => handleVariantFieldChange(vid, f, val.toUpperCase())}
                    isSaving={savingVariantField?.variantId === (v.id || v.tempId) && savingVariantField?.field === 'sku'}
                    error={validationErrors?.[`variant_sku_${v.id || v.tempId}`]}
                    setHasChanges={setHasChanges}
                  />
                </td>
                <td>
                  <InlineCell
                    value={v.price}
                    variantId={v.id || v.tempId}
                    field="price"
                    type="number"
                    onSave={handleVariantFieldChange}
                    isSaving={savingVariantField?.variantId === (v.id || v.tempId) && savingVariantField?.field === 'price'}
                    error={validationErrors?.[`variant_price_${v.id || v.tempId}`]}
                    setHasChanges={setHasChanges}
                  />
                </td>
                <td>
                  <InlineCell
                    value={v.meta?.inventory}
                    variantId={v.id || v.tempId}
                    field="inventory"
                    type="number"
                    onSave={(vid, f, val) => handleVariantFieldChange(vid, "inventory", parseInt(val) || 0)}
                    isSaving={savingVariantField?.variantId === (v.id || v.tempId) && savingVariantField?.field === 'inventory'}
                    setHasChanges={setHasChanges}
                  />
                </td>
                <td style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <button
                    type="button"
                    className="variantEditIconBtn"
                    onClick={() => handleToggleExpand(v)}
                    title="Edit variant details"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </button>
                  {onDeleteVariant && (
                    <button
                      type="button"
                      className="variantEditIconBtn"
                      style={{ color: '#e53e3e' }}
                      onClick={() => onDeleteVariant(variantId)}
                      title="Delete variant"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                        <path d="M10 11v6"></path>
                        <path d="M14 11v6"></path>
                        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path>
                      </svg>
                    </button>
                  )}
                </td>
              </tr>
              {isVariantExpanded && (
                <tr className="variantExpandedRow">
                  <td colSpan={7}>
                    <div className="variantInlineForm">
                      <VariantInlineEditForm
                        variant={v}
                        onCancel={() => setExpandedVariantId(null)}
                        setImagePickerVariant={setImagePickerVariant}
                        setShowImagePickerModal={setShowImagePickerModal}
                        onUpdate={(updatedVariant) => {
                          const vid = updatedVariant.id || updatedVariant.tempId;
                          setAllVariants((prev) =>
                            prev.map((item) => {
                              const itemId = item.id || item.tempId;
                              if (itemId !== vid) return item;
                              return {
                                ...item,
                                ...updatedVariant,
                                meta: {
                                  ...(item.meta || {}),
                                  inventory: updatedVariant.inventory || 0
                                }
                              };
                            })
                          );
                          setHasChanges(true);
                        }}
                        onSave={(updatedVariant) => {
                          const vid = updatedVariant.id || updatedVariant.tempId;
                          setAllVariants((prev) =>
                            prev.map((item) => {
                              const itemId = item.id || item.tempId;
                              if (itemId !== vid) return item;
                              return {
                                ...item,
                                ...updatedVariant,
                                meta: {
                                  ...(item.meta || {}),
                                  inventory: updatedVariant.inventory || 0
                                }
                              };
                            })
                          );
                          setExpandedVariantId(null);
                          setHasChanges(true);
                        }}
                      />
                    </div>
                  </td>
                </tr>
              )}
              </React.Fragment>
            );
            })}
          </tbody>
        </table>
      </div>
      <div className="variantSummary">
        Total Inventory: {calculateTotalInventory()}
      </div>
    </div>
  );
};

function ApprovalEditorView({ product: initialProduct, onClose, onApprove, onReject, onRequestChanges }) {
  const [approvalNotes, setApprovalNotes] = useState('');
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [reasonModalAction, setReasonModalAction] = useState(null);
  const [missingFields, setMissingFields] = useState([]);
  const [productData, setProductData] = useState(null);
  const [productLoading, setProductLoading] = useState(false);
  const [auditHistory, setAuditHistory] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [expandedAuditEntries, setExpandedAuditEntries] = useState({});
  const [updatedFields, setUpdatedFields] = useState([]);
  // Structured snapshot-vs-current diff from the backend. Null until it loads, and
  // for older backends that don't send it — the panel falls back to updatedFields.
  const [changes, setChanges] = useState(null);
  const [editors, setEditors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [adminEditReason, setAdminEditReason] = useState('');
  const [showAdminEditReasonModal, setShowAdminEditReasonModal] = useState(false);
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const [allVariants, setAllVariants] = useState([]);
  const [deletedVariantIds, setDeletedVariantIds] = useState(new Set());
  const [allMediaItems, setAllMediaItems] = useState([]);
  const [displayedProduct, setDisplayedProduct] = useState(null);
  const [formData, setFormData] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [newImageAlt, setNewImageAlt] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageUploadError, setImageUploadError] = useState('');
  const [userRole, setUserRole] = useState(null);
  const [selectedMediaIds, setSelectedMediaIds] = useState([]);

  const handleVariantImageChange = async (variantId, imageId) => {
    const isNewVariant = !variantId || (typeof variantId === 'string' && variantId.startsWith('temp-'));
    const isTempImage = !imageId || String(imageId).startsWith('temp-');

    setShowImagePickerModal(false);

    const previousMediaItems = allMediaItems;
    const previousVariants = allVariants;
    const sourceImage = allMediaItems.find(m => m.id === imageId);

    // Optimistic: this variant releases whatever image it held and joins the chosen
    // one. It must NOT displace the other variants already showing that image — one
    // picture can be the image for many variants.
    setAllMediaItems(prev => assignVariantToMedia(prev, variantId, imageId));
    setAllVariants(prev => prev.map(v => {
      const vid = v.id || v.tempId;
      if (vid === variantId) return { ...v, image_id: imageId || null };
      // Deliberately NOT clearing other variants pointing at the same image — that
      // line enforced "one variant per image", the very limit being removed here.
      return v;
    }));
    setHasUnsavedChanges(true);

    if (isNewVariant) return;

    try {
      let realImageId = imageId;

      // Image not yet persisted — flush media to DB first to get a real UUID
      if (isTempImage) {
        // mediaPayloadItem sends variant_ids (authoritative) + variant_id (legacy
        // mirror). Sending the scalar alone rewrote EVERY row to a single variant.
        const mediaPayload = allMediaItems
          .filter(m => m.url)
          .map((m, idx) => ({ ...mediaPayloadItem(m, idx), is_primary: m.is_primary || false }));
        const saveResp = await updateProduct(product.id, { media: mediaPayload });
        const returnedMedia = saveResp?.product?.media || saveResp?.media || [];
        const matched = returnedMedia.find(m => m.url === sourceImage?.url);
        if (!matched?.id) {
          alert('Could not get image ID after save — please try again');
          setAllMediaItems(previousMediaItems);
          setAllVariants(previousVariants);
          return;
        }
        setAllMediaItems(prev => prev.map(m => {
          const found = returnedMedia.find(
            r => r.url === m.url && mediaVariantIds(r).length === 0 && !m.id
          );
          return found ? { ...m, id: found.id } : m;
        }));
        realImageId = matched.id;
      }

      const response = await assignVariantImage(product.id, variantId, { image_id: realImageId, reason: 'Admin assigned variant image during review' });
      const returnedImage = response?.image;

      if (returnedImage?.id) {
        // Reconcile against the SAME additive rule as the optimistic write, so the
        // server response cannot re-introduce the single-variant assumption.
        const cleared = previousMediaItems;

        let finalMedia;
        if (returnedImage.id === realImageId) {
          // Case A: pure in-place move
          finalMedia = assignVariantToMedia(cleared, variantId, realImageId);
        } else {
          // Case B: the server returned a DIFFERENT id than we sent.
          //
          // This used to mean "the backend copied the image into a second row" — the
          // old workaround for one-variant-per-image. It no longer does that: an image
          // already on another variant is simply linked again, so one picture stays one
          // row. Treat an unexpected id as authoritative rather than as a copy: adopt
          // the row it names, and never fabricate a duplicate tile.
          const existing = cleared.find(m => String(m.id) === String(returnedImage.id));
          const withRow = existing
            ? cleared
            : [
                ...cleared,
                {
                  id: returnedImage.id,
                  url: returnedImage.url || sourceImage?.url || '',
                  alt: returnedImage.alt_text || sourceImage?.alt || '',
                  is_primary: returnedImage.is_primary || false,
                  variant_ids: [],
                  variant_id: null,
                  position: returnedImage.position ?? cleared.length,
                  _isNew: false,
                },
              ];
          finalMedia = assignVariantToMedia(withRow, variantId, returnedImage.id);
        }

        setAllMediaItems(finalMedia);

        if (returnedImage.id === realImageId) {
          // Case A: pure move — update target, clear source
          setAllVariants(prev => prev.map(v => {
            const vid = v.id || v.tempId;
            if (vid === variantId) return { ...v, image_id: returnedImage.id };
            if (v.image_id === realImageId) return { ...v, image_id: null };
            return v;
          }));
        } else {
          // Case B: copy created — restore all from pre-optimistic snapshot,
          // only set the target variant's image_id to the new copy UUID.
          setAllVariants(
            previousVariants.map(v => {
              const vid = v.id || v.tempId;
              if (vid === variantId) return { ...v, image_id: returnedImage.id };
              return v;
            })
          );
        }
      }
    } catch (error) {
      console.error('Error assigning image to variant:', error);
      setAllMediaItems(previousMediaItems);
      setAllVariants(previousVariants);
      alert(`Failed to assign image: ${error.message || 'Unknown error'}`);
    }
  };
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setAllMediaItems((items) => {
        const oldIndex = items.findIndex((item) => (item.id || item.url) === active.id);
        const newIndex = items.findIndex((item) => (item.id || item.url) === over.id);

        const newItems = arrayMove(items, oldIndex, newIndex);
        // First item is primary; update position to match new array order
        return newItems.map((item, idx) => ({
          ...item,
          is_primary: idx === 0,
          position: idx,
        }));
      });
      setHasUnsavedChanges(true);
    }
  };

  const handleRemoveImage = (index) => {
    console.log('[ApprovalEditorView] handleRemoveImage called for index:', index);
    setAllMediaItems(prev => {
      const removed = prev[index];
      const newItems = prev.filter((_, i) => i !== index);
      // Ensure we still have a primary image if any left
      if (newItems.length > 0 && !newItems.some(img => img.is_primary)) {
        newItems[0].is_primary = true;
      }
      return newItems;
    });
    setHasUnsavedChanges(true);
  };

  const toggleImageSelection = (id) => {
    setSelectedMediaIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkRemoveImages = () => {
    if (selectedMediaIds.length === 0) return;
    if (!window.confirm(`Are you sure you want to remove ${selectedMediaIds.length} selected images?`)) return;

    setAllMediaItems(prev => {
      const newItems = prev.filter(img => !selectedMediaIds.includes(img.id || img.url));
      // Ensure we still have a primary image if any left
      if (newItems.length > 0 && !newItems.some(img => img.is_primary)) {
        newItems[0].is_primary = true;
      }
      return newItems;
    });
    setSelectedMediaIds([]);
    setHasUnsavedChanges(true);
  };

  const [exportingLogs, setExportingLogs] = useState(false);
  const [approvingProduct, setApprovingProduct] = useState(false);
  const [expandedVariantId, setExpandedVariantId] = useState(null);
  const [showImagePickerModal, setShowImagePickerModal] = useState(false);
  const [imagePickerVariant, setImagePickerVariant] = useState(null);
  const [variantGroupBy, setVariantGroupBy] = useState('');
  const [expandedVariantGroups, setExpandedVariantGroups] = useState({});
  const [selectedVariantRowIndexes, setSelectedVariantRowIndexes] = useState(() => new Set());
  const [savingVariantField, setSavingVariantField] = useState({ variantId: null, field: null });
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    setSelectedVariantRowIndexes((prev) => {
      const next = new Set([...prev].filter((i) => i >= 0 && i < allVariants.length));
      if (next.size === prev.size) {
        for (const i of prev) {
          if (!next.has(i)) return next;
        }
        return prev;
      }
      return next;
    });
  }, [allVariants]);

  const toggleProductVariantRowSelection = useCallback((rowIndex) => {
    setSelectedVariantRowIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(rowIndex)) next.delete(rowIndex);
      else next.add(rowIndex);
      return next;
    });
  }, []);

  const handleProductVariantsHeaderSelectionClick = useCallback((e) => {
    e.stopPropagation();
    setSelectedVariantRowIndexes((prev) => {
      const total = allVariants.length;
      if (total === 0) return prev;
      let selectedCount = 0;
      for (let i = 0; i < total; i++) {
        if (prev.has(i)) selectedCount++;
      }
      if (selectedCount === total) return new Set();
      return new Set(Array.from({ length: total }, (_, idx) => idx));
    });
  }, [allVariants.length]);
  
  const isAdmin = userRole === 'admin';
  
  // Variant options state (for multi-variant products)
  const [variantsOptions, setVariantsOptions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [optionValues, setOptionValues] = useState({});
  const [currentOptionValue, setCurrentOptionValue] = useState({});
  const [newOptionName, setNewOptionName] = useState('');

  // Resolve product from backend when opened to ensure fresh data
  const product = productData || initialProduct || {};

  // Normalize backend product detail into the shape this view expects
  const normalizeProductDetail = (p) => {
    if (!p || typeof p !== 'object') return p;
    const firstVariant = Array.isArray(p.variants) && p.variants.length > 0 ? p.variants[0] : null;
    const rawMeta = p.meta || {};
    const filteredMeta = Object.fromEntries(
      Object.entries(rawMeta).filter(([k]) => !['vendor_remarks', 'draft_reason', 'tags', 'inventory', 'price', 'rejection_reason'].includes(k))
    );
    return {
      // Product ID and version
      id: p.id,
      version: p.version || 1,

      // Overview fields
      title: p.title || '',
      description: p.description || '',
      category: p.category?.slug || p.category?.name || '',
      categoryId: p.category?.id || '',
      categoryName: p.category?.name || '',
      tags: Array.isArray(p.meta?.tags) ? p.meta.tags : (Array.isArray(p.tags) ? p.tags : []),

      // Media
      images: Array.isArray(p.media) ? p.media.map(m => ({
        id: m.id,
        url: m.url,
        alt: m.alt_text || m.alt || m.url,
        is_primary: m.is_primary || false,
        // Keep the FULL assignment set. Spreading only the scalar here discarded
        // variant_ids before any UI code ran, which is why a shared image looked
        // unassigned to every variant but the first.
        variant_ids: mediaVariantIds(m),
        variant_id: mediaVariantIds(m)[0] || null
      })) : [],
      media: Array.isArray(p.media) ? p.media.map(m => ({
        id: m.id,
        url: m.url,
        alt_text: m.alt_text || m.alt || m.url,
        is_primary: m.is_primary || false,
        variant_ids: mediaVariantIds(m),
        variant_id: mediaVariantIds(m)[0] || null
      })) : [],

      // Core Details
      features: p.features || '',
      careInstructions: p.care_instructions || '',

      // Inventory & Pricing - from first variant
      inventory: firstVariant?.meta?.inventory ?? 0,
      sku: firstVariant?.sku || '',
      variantName: firstVariant?.variant_name || firstVariant?.name || '',
      price: firstVariant?.price ?? '',
      compareAtPrice: firstVariant?.compare_price ?? '',

      // Shipping & Package Details - from first variant
      weight: firstVariant?.weight_kg ?? '',
      dimensions: {
        length: firstVariant?.length_cm ?? '',
        width: firstVariant?.width_cm ?? '',
        height: firstVariant?.height_cm ?? ''
      },
      hsCode: firstVariant?.hs_code || '',

      // Commissions & Fees
      brandCommission: firstVariant?.commission_pct ?? '',
      gstRate: firstVariant?.gst_rate ?? '',
      merchantSplit: p.merchant_split || '',

      // Vendor info
      vendorId: p.vendor?.id || '',
      vendorName: p.vendor?.brand_name || p.vendor?.company_name || '',
      vendorRemarks: p.meta?.vendor_remarks || p.vendor_remarks || '',

      // Meta Fields - custom fields only
      metaFields: filteredMeta,

      // Variants array (full)
      variants: p.variants || [],
      variantId: firstVariant?.id || '',
      variantVersion: firstVariant?.version || 1,

      // `state` and `terminal_status` are the real Terminal model fields, passed
      // through as-is — the status card/dropdown always reads/writes terminal_status
      // directly, matching ProductDetailView. `approval_status` is the one derived
      // display field (used in the audit-log status-change label), kept clearly
      // apart from the two real fields.
      approval_status: p.state === 'pending'
        ? 'pending_approval'
        : (p.terminal_status || p.state || 'draft'),
      terminal_status: p.terminal_status || 'draft',
      publishedAt: p.published_at || null,
      createdAt: p.created_at || '',
      updatedAt: p.updated_at || '',

      // User info
      createdBy: p.created_by ? {
        id: p.created_by.id,
        name: p.created_by.name
      } : null,
      updatedBy: p.updated_by ? {
        id: p.updated_by.id,
        name: p.updated_by.name
      } : null,

      // Shopify integration
      shopifyId: p.shopify_id || null,
      shopifyVariantId: firstVariant?.shopify_variant_id || null,

      // Store the full API response for reference
      _apiProduct: p
    };
  };

  useEffect(() => {
    let cancelled = false;
    const fetchProduct = async () => {
      if (!initialProduct?.id) return;
      try {
        setProductLoading(true);
        const resp = await getProductDetail(initialProduct.id);
        if (cancelled) return;
        const data = resp?.product || resp;
        if (data && typeof data === 'object') {
          const normalized = normalizeProductDetail(data);
          setProductData(normalized);

          // Store all variants
          if (data.variants && data.variants.length > 0) {
            setAllVariants(data.variants);
            setSelectedVariantIndex(0);
            setDeletedVariantIds(new Set()); // Reset deleted variants tracking
          }

          // Store all media items — preserve id and position for save
          const allMedia = (data.media || []).map((media, idx) => ({
            id: media.id,
            url: media.url,
            alt: media.alt_text || media.url,
            is_primary: media.is_primary || false,
            variant_ids: mediaVariantIds(media),
            variant_id: mediaVariantIds(media)[0] || null,
            position: media.position ?? idx,
          }));
          setAllMediaItems(allMedia);

          // Set displayed product with filtered media for first variant
          if (data.variants && data.variants.length > 0) {
            const firstVariant = data.variants[0];
            setDisplayedProduct({
              ...normalized,
              images: mediaForVariantView(allMedia, firstVariant.id),
              sku: firstVariant.sku || '',
              variantName: firstVariant.variant_name || firstVariant.name || '',
              price: firstVariant.price || '',
              compareAtPrice: firstVariant.compare_price || '',
              inventory: firstVariant.meta?.inventory || 0,
              weight: firstVariant.weight_kg || '',
              dimensions: {
                length: firstVariant.length_cm || '',
                width: firstVariant.width_cm || '',
                height: firstVariant.height_cm || ''
              },
              hsCode: firstVariant.hs_code || '',
              brandCommission: firstVariant.commission_pct || ''
            });
          } else {
            setDisplayedProduct(normalized);
          }
        }

        // Use backend-provided missing fields if available
        if (Array.isArray(resp?.missing_fields) && resp.missing_fields.length > 0) {
          setMissingFields(resp.missing_fields);
        }
        
        // Initialize form data for editing
        initializeFormData(data);
      } catch (e) {
        console.error('Error fetching product details:', e);
      } finally {
        if (!cancelled) setProductLoading(false);
      }
    };
    fetchProduct();
    return () => { cancelled = true; };
  }, [initialProduct?.id]);

  // Initialize form data from product
  const initializeFormData = (data) => {
    if (!data) return;
    
    const firstVariant = Array.isArray(data.variants) && data.variants.length > 0 ? data.variants[0] : null;
    const hasMultipleVariants = Array.isArray(data.variants) && data.variants.length > 1;
    
    setFormData({
      // Product fields
      title: data.title || '',
      description: data.description || '',
      features: data.features || '',
      care_instructions: data.care_instructions || '',
      category_id: data.category?.id || '',
      tags: Array.isArray(data.meta?.tags) ? data.meta.tags : (Array.isArray(data.tags) ? data.tags : []),
      vendor_remarks: data.meta?.vendor_remarks || data.vendor_remarks || '',
      terminal_status: data.terminal_status || 'draft',

      // Variant fields (from first variant)
      sku: firstVariant?.sku || '',
      variant_name: firstVariant?.variant_name || firstVariant?.name || '',
      price: firstVariant?.price || '',
      compare_price: firstVariant?.compare_price || '',
      inventory: firstVariant?.meta?.inventory ?? 0,
      weight_kg: firstVariant?.weight_kg || '',
      length_cm: firstVariant?.length_cm || '',
      width_cm: firstVariant?.width_cm || '',
      height_cm: firstVariant?.height_cm || '',
      hs_code: firstVariant?.hs_code || '',
      commission_pct: firstVariant?.commission_pct || '',
      gst_rate: firstVariant?.gst_rate ?? '',
      
      // Has variants flag from backend
      hasVariants: hasMultipleVariants,
      
      // Meta fields
      metaFields: data.meta ? Object.fromEntries(
        Object.entries(data.meta).filter(([k]) => 
          !['vendor_remarks', 'draft_reason', 'tags', 'inventory', 'price', 'rejection_reason'].includes(k)
        )
      ) : {}
    });
    
    // NOTE: option pre-population is handled by the dedicated useEffect that watches
    // formData.hasVariants + variantsOptions + allVariants.
    
    // Preserve id and position so saves always send them back to backend.
    const allImages = (data.media || []).map((media, idx) => ({
      id: media.id,
      url: media.url,
      alt: media.alt_text || media.alt || '',
      is_primary: media.is_primary || false,
      // Full assignment set — the scalar names only the first variant, so a shared
      // image rendered as "unassigned" for every other variant in the table.
      variant_ids: mediaVariantIds(media),
      variant_id: mediaVariantIds(media)[0] || null,
      position: media.position ?? idx,
      _isNew: false,
    }));
    setAllMediaItems(allImages);
  };

  // Fetch review summary (missing fields + recent vendor changes)
  useEffect(() => {
    if (!initialProduct?.id) return;

    let cancelled = false;
    const fetchReviewSummary = async () => {
      try {
        const resp = await getProductReviewSummary(initialProduct.id);
        if (cancelled) return;

        // Update missing fields from backend
        if (Array.isArray(resp?.missing_fields)) {
          setMissingFields(resp.missing_fields);
        }

        // Update recent vendor changes from backend. Drop workflow-only and no-op
        // entries here so the banner isn't rendered empty when everything in the
        // payload gets filtered out at render time.
        if (Array.isArray(resp?.recent_vendor_changes)) {
          const meaningful = resp.recent_vendor_changes.filter((f) => {
            if (NON_CONTENT_CHANGE_FIELDS.has(f.field)) return false;
            // Action-based entries (media add/remove) carry no old/new pair.
            if (f.action) return true;
            return isMeaningfulChange(f.old, f.new);
          });
          setUpdatedFields(meaningful);
        }

        // The structured diff. Absent on older backends, in which case the panel
        // renders the flat list above instead.
        setChanges(resp?.changes ?? null);
        setEditors(Array.isArray(resp?.editors) ? resp.editors : []);
      } catch (e) {
        console.error('Error fetching review summary:', e);
      }
    };

    fetchReviewSummary();
    return () => { cancelled = true; };
  }, [initialProduct?.id]);

  // Fetch audit history when product changes
  // Fetch audit history function (extracted so it can be called from multiple places)
  const fetchAuditHistory = async () => {
    if (!product?.id) return;

    try {
      setAuditLoading(true);
      const resp = await getProductAuditHistory(product.id);
      setAuditHistory(resp.audit_history || []);
    } catch (e) {
      console.error('Error fetching audit history:', e);
      setAuditHistory([]);
    } finally {
      setAuditLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditHistory();
  }, [product?.id]);

  // Fetch user role
  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const roleData = await getCurrentUserRole();
        setUserRole(roleData?.role);
      } catch (err) {
        console.error('Error fetching user role:', err);
      }
    };
    fetchUserRole();
  }, []);

  // Handle export audit logs
  const handleExportLogs = async () => {
    if (!product?.id) return;
    
    setExportingLogs(true);
    try {
      await exportProductLogs(product.id, product.title || 'product');
    } catch (err) {
      console.error('Error exporting logs:', err);
    } finally {
      setExportingLogs(false);
    }
  };

  // Load categories for proper display
  useEffect(() => {
    let isCancelled = false;
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const resp = await listCategories();
        if (isCancelled) return;
        const items = Array.isArray(resp?.categories) ? resp.categories : [];
        setCategories(items);
        // Also load variant options
        if (resp && resp.variants_options) {
          setVariantsOptions(resp.variants_options);
        }
      } catch (e) {
        console.error('Error fetching categories:', e);
      } finally {
        if (!isCancelled) setCategoriesLoading(false);
      }
    };
    fetchCategories();
    return () => { isCancelled = true; };
  }, []);

  // Normalize option names so backend differences like "Option 1" vs "option 1" still match
  const normalizeOptionName = (name) => {
    if (!name) return '';
    return String(name)
      .trim()
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ');
  };

  // Tracks whether we've already pre-populated selectedOptions/optionValues from existing variants
  const [optionsInitialized, setOptionsInitialized] = useState(false);
  // Track initial option values (from existing variants) to detect new additions
  const [initialOptionValues, setInitialOptionValues] = useState({});

  // Initialize options from product data once variantsOptions are loaded
  useEffect(() => {
    if (optionsInitialized) return;
    if (!formData?.hasVariants) return;
    if (!variantsOptions || variantsOptions.length === 0) return;
    if (!allVariants || allVariants.length === 0) return;

    // Prefer explicit option_values; fall back to meta.option_values
    const optionValuesByNormalizedName = new Map(); // normalizedName -> Set(values)

    allVariants.forEach((variant) => {
      // Accept canonical list `[{optionName, name}, ...]`, legacy dict, or legacy `option1`/`option1_name`.
      const meta = { ...(variant?.meta || {}) };
      if (variant?.option_values !== undefined) meta.option_values = variant.option_values;
      const normalized = normalizeVariantOptions(meta);

      normalized.forEach(({ optionName: rawName, name: rawValue }) => {
        const normalizedName = normalizeOptionName(rawName);
        if (!normalizedName) return;

        if (!optionValuesByNormalizedName.has(normalizedName)) {
          optionValuesByNormalizedName.set(normalizedName, new Set());
        }

        if (rawValue && String(rawValue).trim() !== '') {
          optionValuesByNormalizedName.get(normalizedName).add(String(rawValue).trim());
        }
      });
    });

    if (optionValuesByNormalizedName.size === 0) return;

    const selectedOptionIds = [];
    const optionValuesMap = {}; // optionId -> string[]

    variantsOptions.forEach((opt) => {
      const normalizedOptName = normalizeOptionName(opt?.name);
      const valuesSet = optionValuesByNormalizedName.get(normalizedOptName);
      if (!valuesSet) return;

      selectedOptionIds.push(opt.id);
      optionValuesMap[opt.id] = Array.from(valuesSet);
    });

    if (selectedOptionIds.length === 0) return;

    setSelectedOptions(selectedOptionIds.slice(0, 3));
    setOptionValues(optionValuesMap);
    setInitialOptionValues(optionValuesMap); // Store initial values
    setCurrentOptionValue(prev => {
      const next = { ...prev };
      selectedOptionIds.slice(0, 3).forEach(id => {
        if (next[id] === undefined) next[id] = '';
      });
      return next;
    });

    setOptionsInitialized(true);
  }, [optionsInitialized, formData?.hasVariants, variantsOptions, allVariants]);

  // Reset optionsInitialized when opening a different product
  useEffect(() => {
    setOptionsInitialized(false);
  }, [initialProduct?.id]);

  // Missing fields are now provided by backend; no client-side computation needed
  // (removed checkMissingFields useEffect)

  // Handle form field changes
  const handleFieldChange = (field, value) => {
    // Special handling for hasVariants toggle
    if (field === 'hasVariants') {
      setFormData(prev => ({
        ...prev,
        hasVariants: value
      }));
      
      // If enabling variants and only one variant exists, keep it
      // If disabling variants and multiple variants exist, warn user
      if (!value && allVariants.length > 1) {
        if (!window.confirm('Disabling variants will keep only the first variant. Other variants will be removed. Continue?')) {
          return;
        }
        // Keep only first variant
        setAllVariants([allVariants[0]]);
        setSelectedVariantIndex(0);
      } else if (value && allVariants.length === 0) {
        // If enabling variants and no variants exist, create default variant
        const newVariant = {
          id: null,
          sku: formData.sku || '',
          variant_name: '',
          price: formData.price || '',
          compare_price: formData.compare_price || '',
          weight_kg: formData.weight_kg || '',
          length_cm: formData.length_cm || '',
          width_cm: formData.width_cm || '',
          height_cm: formData.height_cm || '',
          hs_code: formData.hs_code || '',
          commission_pct: formData.commission_pct || '',
          meta: {
            inventory: parseInt(formData.inventory) || 0
          }
        };
        setAllVariants([newVariant]);
        setSelectedVariantIndex(0);
      }
      
      setHasUnsavedChanges(true);
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // If editing a variant-specific field, also update the allVariants array
    const variantFields = ['sku', 'variant_name', 'price', 'compare_price', 'inventory', 'weight_kg', 'length_cm', 'width_cm', 'height_cm', 'hs_code', 'commission_pct', 'gst_rate'];
    if (variantFields.includes(field) && selectedVariantIndex >= 0 && allVariants.length > selectedVariantIndex) {
      const updatedVariants = [...allVariants];
      const variant = updatedVariants[selectedVariantIndex];
      
      if (field === 'inventory') {
        variant.meta = variant.meta || {};
        variant.meta.inventory = parseInt(value) || 0;
      } else {
        variant[field] = value;
      }
      
      setAllVariants(updatedVariants);
    }
    
    setHasUnsavedChanges(true);
  };

  // Handle adding an option
  const handleAddOption = (optionId) => {
    if (selectedOptions.length >= 3) {
      alert('Maximum 3 options allowed');
      return;
    }
    if (!selectedOptions.includes(optionId)) {
      setSelectedOptions(prev => [...prev, optionId]);
      setOptionValues(prev => ({ ...prev, [optionId]: [] }));
      setCurrentOptionValue(prev => ({ ...prev, [optionId]: '' }));
    }
  };

  const handleAddCustomOption = () => {
    const name = newOptionName.trim();
    if (!name) return;
    if (selectedOptions.length >= 3) {
      alert('Maximum 3 options allowed');
      return;
    }
    const existing = variantsOptions.find(o => o.name.toLowerCase() === name.toLowerCase());
    if (existing) {
      handleAddOption(existing.id);
      setNewOptionName('');
      return;
    }
    const tempId = 'temp-' + Date.now() + '-' + Math.random().toString(36).slice(2);
    const newOpt = { id: tempId, name };
    setVariantsOptions(prev => [...prev, newOpt]);
    setSelectedOptions(prev => [...prev, tempId]);
    setOptionValues(prev => ({ ...prev, [tempId]: [] }));
    setCurrentOptionValue(prev => ({ ...prev, [tempId]: '' }));
    setNewOptionName('');
  };

  // Handle removing an option
  const handleRemoveOption = (optionId) => {
    setSelectedOptions(prev => prev.filter(id => id !== optionId));
    setOptionValues(prev => {
      const newValues = { ...prev };
      delete newValues[optionId];
      return newValues;
    });
    setCurrentOptionValue(prev => {
      const newValues = { ...prev };
      delete newValues[optionId];
      return newValues;
    });
  };

  // Handle adding a value to an option
  const handleAddOptionValue = (optionId, value) => {
    if (value && value.trim()) {
      setOptionValues(prev => ({
        ...prev,
        [optionId]: [...(prev[optionId] || []), value.trim()]
      }));
      setCurrentOptionValue(prev => ({ ...prev, [optionId]: '' }));
    }
  };

  // Handle removing a value from an option
  const handleRemoveOptionValue = (optionId, valueIndex) => {
    setOptionValues(prev => ({
      ...prev,
      [optionId]: prev[optionId].filter((_, i) => i !== valueIndex)
    }));
  };

  // Handle key press in option value input
  const handleOptionValueKeyPress = (e, optionId) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddOptionValue(optionId, currentOptionValue[optionId]);
    }
  };

  // Check if there are new option values added by user
  const hasNewOptionValues = () => {
    return selectedOptions.some(optionId => {
      const currentValues = optionValues[optionId] || [];
      const initialValues = initialOptionValues[optionId] || [];
      // Check if there are any values in current that aren't in initial
      return currentValues.some(val => !initialValues.includes(val));
    });
  };

  // Generate variants from selected options - calls backend API
  const handleGenerateVariants = async () => {
    if (selectedOptions.length === 0) {
      alert('Please add at least one option with values');
      return;
    }

    // Check if there are any new values
    const hasNewValues = selectedOptions.some(optionId => {
      const currentValues = optionValues[optionId] || [];
      const initialValues = initialOptionValues[optionId] || [];
      return currentValues.some(val => !initialValues.includes(val));
    });

    if (!hasNewValues) {
      alert('No new option values to generate variants from');
      return;
    }

    try {
      // Build options payload for the API
      const optionsPayload = selectedOptions.map(optionId => {
        const option = variantsOptions.find(o => o.id === optionId);
        return {
          id: optionId,
          name: option?.name || '',
          values: optionValues[optionId] || []
        };
      });

      // Call the backend API to save options and auto-generate variants
      const response = await updateProductOptions(product.id, {
        options: optionsPayload,
        reason: 'Generated variants from product options'
      });

      console.log('[ApprovalEditorView] Options update response:', response);

      // Use the returned variants from the API (they now have real IDs)
      if (response.variants && Array.isArray(response.variants)) {
        // Merge returned variants with existing ones, avoiding duplicates
        const existingIds = new Set(allVariants.map(v => v.id).filter(Boolean));
        const newVariants = response.variants.filter(v => !existingIds.has(v.id));

        setAllVariants(prev => [...prev, ...newVariants]);

        alert(`Successfully generated ${response.variants_created || newVariants.length} new variants`);
      }

      // Update initial values to include the new ones
      setInitialOptionValues(optionValues);
      setHasUnsavedChanges(true);
    } catch (error) {
      console.error('[ApprovalEditorView] Error generating variants:', error);
      alert(`Error generating variants: ${error.message || 'Unknown error'}`);
    }
  };

  // Add new variant (manually, without options)
  const handleAddVariant = () => {
    const newVariant = {
      id: null, // New variant, no ID yet - will get one on save
      sku: '',
      variant_name: '',
      price: formData.price || '',
      compare_price: formData.compare_price || '',
      weight_kg: formData.weight_kg || '',
      length_cm: formData.length_cm || '',
      width_cm: formData.width_cm || '',
      height_cm: formData.height_cm || '',
      hs_code: formData.hs_code || '',
      commission_pct: formData.commission_pct || '',
      meta: {
        inventory: parseInt(formData.inventory) || 0
      }
    };

    setAllVariants(prev => [...prev, newVariant]);
    setSelectedVariantIndex(allVariants.length); // Select the new variant
    setHasUnsavedChanges(true);
  };

  // Delete variant by ID (used by VariantTable)
  const handleDeleteVariant = (variantId) => {
    if (allVariants.length <= 1) {
      alert('Cannot remove the last variant. Products must have at least one variant.');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this variant? This cannot be undone.')) {
      return;
    }
    const isNew = !variantId || (typeof variantId === 'string' && variantId.startsWith('new-'));
    if (!isNew) {
      setDeletedVariantIds(prev => new Set([...prev, variantId]));
    }
    const updated = allVariants.filter(v => (v.id || v.tempId) !== variantId);
    setAllVariants(updated);
    setHasUnsavedChanges(true);
  };

  // Remove variant
  const handleRemoveVariant = (index) => {
    if (allVariants.length <= 1) {
      alert('Cannot remove the last variant. Products must have at least one variant.');
      return;
    }

    if (!window.confirm('Are you sure you want to remove this variant?')) {
      return;
    }

    const variantToRemove = allVariants[index];

    // Track deleted variant ID if it exists (backend variant)
    if (variantToRemove.id) {
      setDeletedVariantIds(prev => new Set([...prev, variantToRemove.id]));
    }

    // Remove the variant from allVariants
    const updatedVariants = allVariants.filter((_, i) => i !== index);
    setAllVariants(updatedVariants);

    // Calculate new selected index
    let newIndex = selectedVariantIndex;
    if (index < selectedVariantIndex) {
      // If we deleted a variant before the current one, shift index down
      newIndex = selectedVariantIndex - 1;
    } else if (index === selectedVariantIndex) {
      // If we deleted the current variant, select the previous one (or first if deleting first)
      newIndex = Math.max(0, index - 1);
    }
    // If we deleted a variant after the current one, index stays the same

    setSelectedVariantIndex(newIndex);
    setHasUnsavedChanges(true);
    
    // Load the new selected variant's data into formData
    if (updatedVariants[newIndex]) {
      const selectedVariant = updatedVariants[newIndex];
      setFormData(prev => ({
        ...prev,
        sku: selectedVariant.sku || '',
        variant_name: selectedVariant.variant_name || selectedVariant.name || '',
        price: selectedVariant.price || '',
        compare_price: selectedVariant.compare_price || '',
        inventory: selectedVariant.meta?.inventory || 0,
        weight_kg: selectedVariant.weight_kg || '',
        length_cm: selectedVariant.length_cm || '',
        width_cm: selectedVariant.width_cm || '',
        height_cm: selectedVariant.height_cm || '',
        hs_code: selectedVariant.hs_code || '',
        commission_pct: selectedVariant.commission_pct || ''
      }));
    }
    
    setHasUnsavedChanges(true);
  };

  // Handle tag changes
  const handleTagAdd = (tag) => {
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
      setHasUnsavedChanges(true);
    }
  };

  const handleTagRemove = (index) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index)
    }));
    setHasUnsavedChanges(true);
  };

  // Handle image operations - uploads to S3 and stores the returned URL
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const token = localStorage.getItem('token');
    if (!token) {
      setImageUploadError('Authentication required. Please log in.');
      return;
    }

    setUploadingImage(true);
    setImageUploadError('');

    const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const MAX_IMAGE_SIZE = 25 * 1024 * 1024; // 25MB as per backend

    try {
      const uploadPromises = files.map(async (file) => {
        // Validate file type
        if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
          throw new Error(`${file.name}: Unsupported file type. Please upload JPG, PNG, GIF, or WebP images.`);
        }

        // Validate file size
        if (file.size > MAX_IMAGE_SIZE) {
          throw new Error(`${file.name}: File size exceeds 25MB limit.`);
        }

        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        formDataUpload.append('file_type', 'image');

        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/core/s3/upload/`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formDataUpload
        });

        if (!res.ok) {
          const err = await res.json().catch(() => null);
          throw new Error(err?.error || `Failed to upload ${file.name}`);
        }

        return res.json();
      });

      const uploadedFiles = await Promise.all(uploadPromises);

      // Get variant ID if a variant is selected
      const variantId = selectedVariantIndex >= 0 && allVariants[selectedVariantIndex]
        ? allVariants[selectedVariantIndex].id
        : null;

      // Add uploaded images to the media items array using S3 URLs
      setAllMediaItems((prev) => {
        const shouldSetPrimary = prev.length === 0;
        const newImages = uploadedFiles.map((upload, index) => ({
          id: `temp-${Date.now()}-${Math.random()}`,
          url: upload.file_url, // Use the S3 URL from the upload response
          alt: upload.filename || '',
          is_primary: shouldSetPrimary && index === 0,
          variant_ids: variantId ? [variantId] : [],
          variant_id: variantId,
          _isNew: true
        }));

        return [...prev, ...newImages];
      });

      setHasUnsavedChanges(true);

    } catch (error) {
      console.error('Image upload failed:', error);
      setImageUploadError(error.message || 'Failed to upload images. Please try again.');
    } finally {
      setUploadingImage(false);
      // Clear the file input
      e.target.value = '';
    }
  };

  const handleUpdateImage = (index, field, value) => {
    setAllMediaItems(prev => {
      const updated = [...prev];
      if (field === 'is_primary' && value) {
        // Unset primary from all others
        updated.forEach((img, i) => {
          if (i !== index) img.is_primary = false;
        });
      }
      updated[index][field] = value;
      return updated;
    });
    setHasUnsavedChanges(true);
  };

  // Approve product
  const handleApprove = async (type) => {
    // Tags must not be empty before approving
    const currentTags = formData.tags || [];
    if (!currentTags.length) {
      alert('Please add at least one tag before approving this product');
      return;
    }

    // Validate that no blob URLs are being saved - these are local-only URLs that won't work on Shopify
    if (hasBlobUrls(allMediaItems)) {
      alert('Some images failed to upload properly and cannot be saved. Please remove the failed images and re-upload them.');
      setImageUploadError('Some images have invalid URLs (blob:). Please remove and re-upload them.');
      return;
    }

    setApprovingProduct(true);
    try {
      // If there are unsaved changes, save them first before approving
      if (hasUnsavedChanges) {
        console.log('[ApprovalEditorView] Auto-saving changes before approval...');
        
        // Use allVariants as the source of truth for variant updates
        const updatedVariants = allVariants.map(v => {
          // If this is the currently edited variant, sync with formData
          if (v.id === formData.variantId || v.tempId === formData.variantId) {
            return {
              ...v,
              sku: formData.sku,
              variant_name: formData.variant_name,
              price: formData.price,
              compare_price: formData.compare_price,
              gst_rate: formData.gst_rate,
              meta: { ...v.meta, inventory: parseInt(formData.inventory) || 0 },
              weight_kg: formData.weight_kg,
              length_cm: formData.length_cm,
              width_cm: formData.width_cm,
              height_cm: formData.height_cm,
              hs_code: formData.hs_code,
              commission_pct: formData.commission_pct
            };
          }
          return v;
        });

        const updatePayload = {
          title: formData.title,
          description: formData.description,
          category_id: formData.category_id,
          tags: formData.tags,
          reason: 'Approved with changes',
          media: allMediaItems
            .filter(img => img.url && !img._isNew)
            // mediaPayloadItem carries variant_ids (authoritative) alongside the legacy
            // scalar. Sending only the scalar meant approving a product rewrote every
            // image to a single variant, silently unassigning the rest.
            .map((img, idx) => ({
              ...mediaPayloadItem(img, idx),
              is_primary: img.is_primary || false,
              position: idx,
            })),
          variants: updatedVariants.map(variant => ({
            id: variant.id || null,
            sku: variant.sku,
            variant_name: variant.variant_name || variant.name || '',
            price: parseFloat(variant.price) || null,
            compare_price: parseFloat(variant.compare_price) || null,
            gst_rate: variant.gst_rate !== '' && variant.gst_rate != null ? parseFloat(variant.gst_rate) : null,
            weight_kg: parseFloat(variant.weight_kg) || null,
            length_cm: parseFloat(variant.length_cm) || null,
            width_cm: parseFloat(variant.width_cm) || null,
            height_cm: parseFloat(variant.height_cm) || null,
            hs_code: variant.hs_code || '',
            commission_pct: parseFloat(variant.commission_pct) || null,
            option_values: variant.option_values || variant.meta?.option_values || undefined,
            meta: {
              inventory: parseInt(variant.meta?.inventory) || 0
            }
          }))
        };

        await updateProduct(product.id, updatePayload);
        setHasUnsavedChanges(false);
      }

      const response = await onApprove(product.id, type);

      // Read product status from response (Terminal model uses state + terminal_status)
      if (response?.product) {
        const rp = response.product;
        const derivedApprovalStatus = rp.state === 'pending'
          ? 'pending_approval'
          : (rp.terminal_status || rp.state || 'draft');
        setProductData(prev => prev ? {
          ...prev,
          approval_status: derivedApprovalStatus,
          state: rp.state,
          terminal_status: rp.terminal_status,
        } : prev);
      }

      // Check for shopify_sync_error in the response
      if (response?.shopify_sync_error) {
        alert('Product approved but Shopify sync failed — it will retry on next save.');
      }

      onClose();
    } catch (error) {
      console.error('Error approving product:', error);
      alert(`Failed to approve product: ${error.message || 'Unknown error'}`);
    } finally {
      setApprovingProduct(false);
    }
  };

  const handleReject = () => {
    setReasonModalAction('reject');
    setShowReasonModal(true);
  };

  const handleRequestChanges = () => {
    setReasonModalAction('request_changes');
    setShowReasonModal(true);
  };

  const handleReasonSubmit = (reason) => {
    if (reasonModalAction === 'reject') {
      onReject(product.id, reason);
    } else if (reasonModalAction === 'request_changes') {
      onRequestChanges(product.id, reason);
    }
    setShowReasonModal(false);
    setReasonModalAction(null);
    onClose();
  };

  const handleCloseReasonModal = () => {
    setShowReasonModal(false);
    setReasonModalAction(null);
  };

  // Handle saving edits with audit logging
  const handleSaveEdit = async () => {
    console.log('[ApprovalEditorView] handleSaveEdit called');
    // Show reason modal first
    setShowAdminEditReasonModal(true);
  };

  // Helper function to normalize empty values (treats null, undefined, '', '—' as the same)
  const normalizeValue = (value) => {
    if (value === null || value === undefined || value === '' || value === '—') {
      return null;
    }
    return value;
  };



  const confirmAdminEdit = async () => {
    console.log('[ApprovalEditorView] confirmAdminEdit called');
    console.log('[ApprovalEditorView] adminEditReason:', adminEditReason);
    console.log('[ApprovalEditorView] formData:', formData);

    if (!adminEditReason || !adminEditReason.trim()) {
      alert('Please provide a reason for editing this product');
      return;
    }

    // Validate that no blob URLs are being saved - these are local-only URLs that won't work on Shopify
    if (hasBlobUrls(allMediaItems)) {
      alert('Some images failed to upload properly and cannot be saved. Please remove the failed images and re-upload them.');
      setImageUploadError('Some images have invalid URLs (blob:). Please remove and re-upload them.');
      return;
    }

    try {
      // First, update the currently selected variant in allVariants with current form data
      // Only do this for single variant products as the VariantTable handles multiples
      const updatedVariants = [...allVariants];
      if (!formData.hasVariants && selectedVariantIndex >= 0 && selectedVariantIndex < updatedVariants.length) {
        updatedVariants[selectedVariantIndex] = {
          ...updatedVariants[selectedVariantIndex],
          sku: formData.sku,
          variant_name: formData.variant_name,
          price: formData.price,
          compare_price: formData.compare_price,
          gst_rate: formData.gst_rate,
          weight_kg: formData.weight_kg,
          length_cm: formData.length_cm,
          width_cm: formData.width_cm,
          height_cm: formData.height_cm,
          hs_code: formData.hs_code,
          commission_pct: formData.commission_pct,
          meta: {
            inventory: parseInt(formData.inventory) || 0
          }
        };
      }

      // Prepare the update payload from form data
      const updatePayload = {
        title: formData.title,
        description: formData.description,
        features: formData.features,
        care_instructions: formData.care_instructions,
        category_id: formData.category_id,
        terminal_status: normalizeValue(formData.terminal_status),
        reason: adminEditReason.trim(),
        meta: {
          ...formData.metaFields,
          tags: formData.tags,
          vendor_remarks: formData.vendor_remarks
        },
        media: allMediaItems
          .filter(img => img.url)
          .map((img, idx) => ({
            ...mediaPayloadItem(img, idx),
            is_primary: img.is_primary || false,
            position: idx,
          })),
        // Send ALL variants, not just the selected one
        // Include newly created variants even with empty fields, and mark deleted ones with _delete
        variants: [
          ...updatedVariants.map(variant => ({
            id: variant.id || null,
            sku: variant.sku || '',
            variant_name: variant.variant_name || variant.name || '',
            price: parseFloat(variant.price) || null,
            compare_price: parseFloat(variant.compare_price) || null,
            gst_rate: variant.gst_rate !== '' && variant.gst_rate != null ? parseFloat(variant.gst_rate) : null,
            weight_kg: parseFloat(variant.weight_kg) || null,
            length_cm: parseFloat(variant.length_cm) || null,
            width_cm: parseFloat(variant.width_cm) || null,
            height_cm: parseFloat(variant.height_cm) || null,
            hs_code: variant.hs_code || '',
            commission_pct: parseFloat(variant.commission_pct) || null,
            option_values: variant.option_values || variant.meta?.option_values || undefined,
            meta: {
              inventory: parseInt(variant.meta?.inventory) || 0
            }
          })),
          // Include deleted variants with _delete flag
          ...Array.from(deletedVariantIds).map(variantId => ({
            id: variantId,
            _delete: true
          }))
        ]
      };

      console.log('[ApprovalEditorView] Updating product ID:', product.id);
      console.log('[ApprovalEditorView] Update payload:', updatePayload);

      // Call backend API
      const response = await updateProduct(product.id, updatePayload);

      console.log('[ApprovalEditorView] Update response:', response);

      alert('Product updated successfully with audit trail!');

      // Close modal
      setShowAdminEditReasonModal(false);
      setAdminEditReason('');
      setHasUnsavedChanges(false);

      const prevSelectedIndex = selectedVariantIndex;
      const resp = await getProductDetail(product.id);
      const data = resp?.product || resp;
      if (data && typeof data === 'object') {
        const normalized = normalizeProductDetail(data);
        setProductData(normalized);

        // Update variants and media
        if (data.variants && data.variants.length > 0) {
          setAllVariants(data.variants);
          const restoredIndex = Math.min(prevSelectedIndex, data.variants.length - 1);
          setSelectedVariantIndex(restoredIndex);
          setDeletedVariantIds(new Set());
        }

        const allMedia = (data.media || []).map((media, idx) => ({
          id: media.id,
          url: media.url,
          alt: media.alt_text || media.url,
          is_primary: media.is_primary || false,
          variant_ids: mediaVariantIds(media),
          variant_id: mediaVariantIds(media)[0] || null,
          position: media.position ?? idx,
        }));
        setAllMediaItems(allMedia);

        // Update displayed product using the restored variant index
        if (data.variants && data.variants.length > 0) {
          const restoredIdx = Math.min(prevSelectedIndex, data.variants.length - 1);
          const displayVariant = data.variants[restoredIdx];
          setDisplayedProduct({
            ...normalized,
            images: allMedia.filter(media =>
              isVariantOnMedia(media, displayVariant.id) || mediaVariantIds(media).length === 0
            ),
            sku: displayVariant.sku || '',
            variantName: displayVariant.variant_name || displayVariant.name || '',
            price: displayVariant.price || '',
            compareAtPrice: displayVariant.compare_price || '',
            inventory: displayVariant.meta?.inventory || 0,
            weight: displayVariant.weight_kg || '',
            dimensions: {
              length: displayVariant.length_cm || '',
              width: displayVariant.width_cm || '',
              height: displayVariant.height_cm || ''
            },
            hsCode: displayVariant.hs_code || '',
            brandCommission: displayVariant.commission_pct || ''
          });
        } else {
          setDisplayedProduct(normalized);
        }
      }

      // Refresh audit history
      await fetchAuditHistory();
      
      // Reinitialize form data
      initializeFormData(data);
    } catch (error) {
      console.error('[ApprovalEditorView] Error saving edit:', error);
      alert(`Error saving changes: ${error.response?.data?.error || error.message}`);
    }
  };

  const cancelAdminEdit = () => {
    setShowAdminEditReasonModal(false);
    setAdminEditReason('');
  };



  // Handle variant selection change
  const handleVariantChange = (variantIndex) => {
    if (variantIndex < 0 || variantIndex >= allVariants.length) return;

    setSelectedVariantIndex(variantIndex);
    const selectedVariant = allVariants[variantIndex];

    // Update formData with selected variant data
    setFormData(prev => ({
      ...prev,
      sku: selectedVariant.sku || '',
      variant_name: selectedVariant.variant_name || selectedVariant.name || '',
      price: selectedVariant.price || '',
      compare_price: selectedVariant.compare_price || '',
      inventory: selectedVariant.meta?.inventory || 0,
      weight_kg: selectedVariant.weight_kg || '',
      length_cm: selectedVariant.length_cm || '',
      width_cm: selectedVariant.width_cm || '',
      height_cm: selectedVariant.height_cm || '',
      hs_code: selectedVariant.hs_code || '',
      commission_pct: selectedVariant.commission_pct || '',
      gst_rate: selectedVariant.gst_rate ?? ''
    }));

    // Update displayed product with selected variant data
    setDisplayedProduct(prev => ({
      ...prev,
      sku: selectedVariant.sku || '',
      variantName: selectedVariant.variant_name || selectedVariant.name || '',
      price: selectedVariant.price || '',
      compareAtPrice: selectedVariant.compare_price || '',
      inventory: selectedVariant.meta?.inventory || 0,
      weight: selectedVariant.weight_kg || '',
      dimensions: {
        length: selectedVariant.length_cm || '',
        width: selectedVariant.width_cm || '',
        height: selectedVariant.height_cm || ''
      },
      hsCode: selectedVariant.hs_code || '',
      brandCommission: selectedVariant.commission_pct || '',
      // Filter images for this variant
      images: allMediaItems.filter(media =>
        isVariantOnMedia(media, selectedVariant.id) || mediaVariantIds(media).length === 0
      )
    }));
  };

  // State for expanded variants
  const [expandedVariants, setExpandedVariants] = useState({});
  const [selectedVariants, setSelectedVariants] = useState(new Set());

  // Toggle variant expansion
  const toggleVariantExpansion = (index) => {
    setExpandedVariants(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Toggle variant selection
  const toggleVariantSelection = (index) => {
    setSelectedVariants(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Calculate total inventory
  const getTotalInventory = () => {
    return allVariants.reduce((total, variant) => {
      return total + (variant.meta?.inventory || 0);
    }, 0);
  };


  // Use displayedProduct for rendering, fallback to product
  const displayProduct = displayedProduct || product;

  const renderOverviewSection = () => (
    <div className="shopifyCard">
      <div className="shopifyCardHeader">
        <h3>Product Information</h3>
      </div>
      <div className="formSection">
        <div className="formGrid">
          <div className="formGroup">
            <label htmlFor="title">Product Title *</label>
            <input
              type="text"
              id="title"
              value={formData.title || ''}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              className="shopifyInput"
            />
          </div>

          <div className="formGroup">
            <label htmlFor="category">Category *</label>
            <select
              id="category"
              value={formData.category_id || ''}
              onChange={(e) => handleFieldChange('category_id', e.target.value)}
              className="shopifySelect"
            >
              <option value="">Select a category</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          <div className="formGroup">
            <label>Vendor</label>
            <div className="readOnlyField">{product.vendorName || 'Not set'}</div>
          </div>
        </div>

        <div className="formGroup">
          <label htmlFor="description">
            Description <span className="required">*</span>
          </label>
          <HTMLEditor
            value={formData.description || ''}
            onChange={(value) => handleFieldChange('description', value)}
            placeholder="Enter a detailed product description with rich formatting (required)"
          />
          {!formData.description && (
            <span className="errorMessage">Description is required</span>
          )}
        </div>

        <div className="formGroup">
          <label>Tags {isAdmin && <span className="required">*</span>}</label>
          <div className="tagsInput">
            <div className="tagsDisplay">
              {(formData.tags || []).map((tag, index) => (
                <span key={index} className="tag">
                  {tag}
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => handleTagRemove(index)}
                      className="tagRemove"
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
              {!isAdmin && (!formData.tags || formData.tags.length === 0) && (
                <span style={{ color: '#999', fontStyle: 'italic' }}>No tags assigned</span>
              )}
            </div>
            {isAdmin && (
              <input
                type="text"
                placeholder="Add a tag and press Enter"
                className="shopifyInput"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleTagAdd(e.target.value);
                    e.target.value = '';
                  }
                }}
              />
            )}
          </div>
        </div>
      </div>


    </div>
  );

  const renderInventoryPricingSection = () => (
    <div className="shopifyCard">
      <div className="shopifyCardHeader">
        <h3>Variants</h3>
      </div>
      <div className="formSection">
        <div className="formGrid">
          <div className="formGroup fullWidth">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={formData.hasVariants}
                onChange={(e) => handleFieldChange('hasVariants', e.target.checked)}
                style={{ width: 'auto', margin: 0 }}
              />
              <span>This product has multiple variants</span>
            </label>
          </div>
        </div>

        {/* Product Options Section - only show if hasVariants is checked */}
        {formData.hasVariants && (
          <div className="formSection" style={{ marginTop: '1.5rem', padding: '1rem', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
            <div style={{ marginBottom: '1rem' }}>
              <h4 style={{ margin: '0 0 0.75rem 0' }}>Product Options</h4>
              {selectedOptions.length < 3 && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  {variantsOptions.length > 0 && (
                    <select
                      value=""
                      onChange={(e) => { if (e.target.value) handleAddOption(e.target.value); }}
                      className="shopifySelect"
                      style={{ width: 'auto', minWidth: '160px' }}
                      disabled={variantsOptions.every(opt => selectedOptions.includes(opt.id))}
                    >
                      <option value="">
                        {variantsOptions.every(opt => selectedOptions.includes(opt.id))
                          ? 'All saved options added'
                          : 'Select saved option'}
                      </option>
                      {variantsOptions
                        .filter(opt => !selectedOptions.includes(opt.id))
                        .map(opt => (
                          <option key={opt.id} value={opt.id}>{opt.name}</option>
                        ))
                      }
                    </select>
                  )}
                  <input
                    type="text"
                    value={newOptionName}
                    onChange={(e) => setNewOptionName(e.target.value)}
                    onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddCustomOption(); } }}
                    placeholder="Type option name (e.g. Size, Color)"
                    className="shopifyInput"
                    style={{ flex: 1, minWidth: '180px' }}
                  />
                  <button
                    type="button"
                    onClick={handleAddCustomOption}
                    className="shopifyButton shopifyButtonPrimary"
                  >
                    + Add
                  </button>
                </div>
              )}
            </div>

            {selectedOptions.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#666', border: '1px dashed #ccc', borderRadius: '4px' }}>
                Select options from the dropdown above to create product variants
              </div>
            ) : (
              <div>
                {selectedOptions.map(optionId => {
                  const option = variantsOptions.find(o => o.id === optionId);
                  const values = optionValues[optionId] || [];

                  return (
                    <div key={optionId} style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <strong>{option?.name || 'Option'}</strong>
                        <button
                          type="button"
                          onClick={() => handleRemoveOption(optionId)}
                          style={{
                            padding: '4px 12px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Remove
                        </button>
                      </div>

                      <div style={{ display: 'flex', gap: '8px', marginBottom: '0.5rem' }}>
                        <input
                          type="text"
                          value={currentOptionValue[optionId] || ''}
                          onChange={(e) => setCurrentOptionValue(prev => ({ ...prev, [optionId]: e.target.value }))}
                          onKeyPress={(e) => handleOptionValueKeyPress(e, optionId)}
                          placeholder="Enter value and press Enter"
                          className="shopifyInput"
                          style={{ flex: 1 }}
                        />
                        <button
                          type="button"
                          onClick={() => handleAddOptionValue(optionId, currentOptionValue[optionId])}
                          className="shopifyButton shopifyButtonPrimary"
                        >
                          Add
                        </button>
                      </div>

                      {values.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {values.map((value, index) => (
                            <div
                              key={index}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '6px 12px',
                                backgroundColor: '#f0f0f0',
                                borderRadius: '20px',
                                fontSize: '14px'
                              }}
                            >
                              <span>{value}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveOptionValue(optionId, index)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: '16px',
                                  color: '#999',
                                  padding: 0,
                                  lineHeight: 1
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}

                <button
                  type="button"
                  onClick={handleGenerateVariants}
                  disabled={!hasNewOptionValues()}
                  className="shopifyButton shopifyButtonPrimary"
                  style={{ marginTop: '1rem' }}
                  title={hasNewOptionValues() ? '' : 'Add new option values to generate additional variants'}
                >
                  Generate New Variants
                </button>
                {!hasNewOptionValues() && (
                  <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
                    Add new values to options above to generate additional variants
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Variants Table or Single Variant Grid */}
        {formData.hasVariants ? (
          <div className="formSection" style={{ marginTop: '1.5rem' }}>
            <h4 style={{ marginBottom: '1rem' }}>Manage Variants</h4>
            <VariantTable
              allVariants={allVariants}
              options={selectedOptions.map(optionId => {
                const option = variantsOptions.find(o => o.id === optionId);
                return { id: optionId, name: option?.name || '', values: optionValues[optionId] || [] };
              })}
              selectedVariantRowIndexes={selectedVariantRowIndexes}
              allMediaItems={allMediaItems}
              variantGroupBy={variantGroupBy}
              expandedVariantGroups={expandedVariantGroups}
              validationErrors={validationErrors}
              savingVariantField={savingVariantField}
              handleVariantFieldChange={(vid, field, value) => {
                setAllVariants(prev => prev.map(v => (v.id === vid || v.tempId === vid) ? { ...v, [field]: value } : v));
                setHasUnsavedChanges(true);
              }}
              toggleProductVariantRowSelection={toggleProductVariantRowSelection}
              handleProductVariantsHeaderSelectionClick={handleProductVariantsHeaderSelectionClick}
              setVariantGroupBy={setVariantGroupBy}
              setExpandedVariantGroups={setExpandedVariantGroups}
              setHasChanges={setHasUnsavedChanges}
              expandedVariantId={expandedVariantId}
              setExpandedVariantId={setExpandedVariantId}
              setAllVariants={setAllVariants}
              setImagePickerVariant={setImagePickerVariant}
              setShowImagePickerModal={setShowImagePickerModal}
              onDeleteVariant={handleDeleteVariant}
            />
          </div>
        ) : (
          <div className="formGrid">
            <div className="formGroup">
              <label htmlFor="sku">SKU *</label>
              <input
                type="text"
                id="sku"
                value={formData.sku || ''}
                onChange={(e) => handleFieldChange('sku', e.target.value)}
                className="shopifyInput"
              />
            </div>

            <div className="formGroup">
              <label htmlFor="inventory">Inventory Quantity *</label>
              <input
                type="number"
                id="inventory"
                value={formData.inventory ?? ''}
                onChange={(e) => handleFieldChange('inventory', e.target.value)}
                className="shopifyInput"
                min="0"
              />
            </div>

            <div className="formGroup">
              <label htmlFor="price">Price (₹) *</label>
              <input
                type="number"
                id="price"
                value={formData.price || ''}
                onChange={(e) => handleFieldChange('price', e.target.value)}
                className="shopifyInput"
                step="0.01"
                min="0"
              />
            </div>

            <div className="formGroup">
              <label htmlFor="comparePrice">Compare at Price (₹)</label>
              <input
                type="number"
                id="comparePrice"
                value={formData.compare_price || ''}
                onChange={(e) => handleFieldChange('compare_price', e.target.value)}
                className="shopifyInput"
                step="0.01"
                min="0"
              />
            </div>

            <div className="formGroup">
              <label htmlFor="gstRate">GST Rate (%)</label>
              <input
                type="number"
                id="gstRate"
                value={formData.gst_rate || ''}
                onChange={(e) => handleFieldChange('gst_rate', e.target.value)}
                className="shopifyInput"
                step="0.01"
                min="0"
                max="100"
              />
            </div>

            <div className="formGroup">
              <label htmlFor="hsCode">HS Code</label>
              <input
                type="text"
                id="hsCode"
                value={formData.hs_code || ''}
                onChange={(e) => handleFieldChange('hs_code', e.target.value)}
                className="shopifyInput"
              />
            </div>
          </div>
        )}
      </div>
      
      <ImagePickerModal 
        showImagePickerModal={showImagePickerModal}
        setShowImagePickerModal={setShowImagePickerModal}
        imagePickerVariant={imagePickerVariant}
        allMediaItems={allMediaItems}
        allVariants={allVariants}
        handleVariantImageChange={handleVariantImageChange}
      />
    </div>
  );

  const renderShippingSection = () => (
    <div className="shopifyCard">
      <div className="shopifyCardHeader">
        <h3>Shipping Details</h3>
      </div>
      <div className="formSection">
        <div className="formGrid">
          <div className="formGroup">
            <label htmlFor="weight">Weight (kg)</label>
            <input
              type="number"
              id="weight"
              value={formData.weight_kg || ''}
              onChange={(e) => handleFieldChange('weight_kg', e.target.value)}
              className="shopifyInput"
              step="0.01"
              min="0"
            />
          </div>

          <div className="formGroup">
            <label htmlFor="length">Length (cm)</label>
            <input
              type="number"
              id="length"
              value={formData.length_cm || ''}
              onChange={(e) => handleFieldChange('length_cm', e.target.value)}
              className="shopifyInput"
              step="0.01"
              min="0"
            />
          </div>

          <div className="formGroup">
            <label htmlFor="width">Width (cm)</label>
            <input
              type="number"
              id="width"
              value={formData.width_cm || ''}
              onChange={(e) => handleFieldChange('width_cm', e.target.value)}
              className="shopifyInput"
              step="0.01"
              min="0"
            />
          </div>

          <div className="formGroup">
            <label htmlFor="height">Height (cm)</label>
            <input
              type="number"
              id="height"
              value={formData.height_cm || ''}
              onChange={(e) => handleFieldChange('height_cm', e.target.value)}
              className="shopifyInput"
              step="0.01"
              min="0"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderMediaSection = () => (
    <div className="shopifyCard">
      <div className="shopifyCardHeader" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3>Product Media</h3>
        {selectedMediaIds.length > 0 && (
          <button
            type="button"
            className="bulkActionButton danger"
            onClick={handleBulkRemoveImages}
            style={{ padding: '4px 12px', fontSize: '0.8rem' }}
          >
            Remove Selected ({selectedMediaIds.length})
          </button>
        )}
      </div>
      <div className="formSection">
        <div className="imageUploader productDetailImageUploader">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={allMediaItems.filter((m, idx, arr) => arr.findIndex(x => x.url === m.url) === idx).map(img => img.id || img.url)}
              strategy={rectSortingStrategy}
            >
              {allMediaItems.filter((m, idx, arr) => arr.findIndex(x => x.url === m.url) === idx).map((image, index) => {
                const assignedVariant = allVariants.find(v =>
                  isVariantOnMedia(image, v.id || v.tempId)
                );

                return (
                  <SortableImage
                    key={image.id || image.url}
                    image={image}
                    index={index}
                    isReadOnly={false}
                    assignedVariant={assignedVariant}
                    onRemove={() => handleRemoveImage(index)}
                    isSelected={selectedMediaIds.includes(image.id || image.url)}
                    onToggleSelect={toggleImageSelection}
                  />
                );
              })}
            </SortableContext>
          </DndContext>

          {true && (
            <>
              <input
                id="fileUploadInput"
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                multiple
                onChange={handleImageUpload}
                disabled={uploadingImage}
                style={{ display: 'none' }}
              />
              <div
                className="imageItem blankImageSlot"
                onClick={() => !uploadingImage && document.getElementById('fileUploadInput').click()}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px dashed #ccc',
                  borderRadius: '8px',
                  cursor: uploadingImage ? 'wait' : 'pointer',
                  minHeight: '150px',
                  backgroundColor: uploadingImage ? '#f0f0f0' : '#fafafa',
                  transition: 'all 0.2s ease',
                  gridColumn: 'span 1'
                }}
                onMouseEnter={(e) => { if (!uploadingImage) { e.currentTarget.style.borderColor = '#00bfae'; e.currentTarget.style.backgroundColor = '#f0faf9'; } }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#ccc'; e.currentTarget.style.backgroundColor = uploadingImage ? '#f0f0f0' : '#fafafa'; }}
              >
                {uploadingImage ? (
                  <span style={{ fontSize: '1rem', color: '#00bfae' }}>⏳ Uploading...</span>
                ) : (
                  <>
                    <span style={{ fontSize: '2rem', color: '#999', lineHeight: 1 }}>+</span>
                    <span style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.5rem' }}>Upload Image</span>
                  </>
                )}
              </div>
              {imageUploadError && (
                <div className="errorMessage" style={{ marginTop: '0.5rem', color: '#dc3545' }}>{imageUploadError}</div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  const renderVendorRemarksSection = () => (
    <div className="shopifyCard">
      <div className="shopifyCardHeader">
        <h3>Vendor Remarks</h3>
      </div>
      <div className="formSection">
        <div className="formGroup">
          <label htmlFor="vendorRemarks">Private Notes</label>
          <textarea
            id="vendorRemarks"
            value={formData.vendor_remarks || ''}
            onChange={(e) => handleFieldChange('vendor_remarks', e.target.value)}
            className="shopifyInput"
            rows="6"
            placeholder="Add any private notes or remarks..."
          />
        </div>
      </div>
    </div>
  );

  // Helpers moved to component scope so other UI can reuse them (updated-fields banner)
  const formatFieldName = (field) => {
    const fieldMap = {
      // Product fields
      'title': 'Title',
      'description': 'Description',
      'category_id': 'Category',
      'Category Id': 'Category',  // Handle formatted field name
      'category': 'Category',
      'status': 'Status',
      'product_state': 'Product State',
      'meta': 'Metadata',

      // Variant fields
      'sku': 'SKU',
      'variant_name': 'Variant Name',
      'price': 'Price',
      'compare_price': 'Compare At Price',
      'weight_kg': 'Weight (kg)',
      'length_cm': 'Length (cm)',
      'width_cm': 'Width (cm)',
      'height_cm': 'Height (cm)',
      'hs_code': 'HS Code',
      'commission_pct': 'Commission (%)',

      // Meta fields (with meta. prefix)
      'meta.inventory': 'Inventory',
      'meta.price': 'Price',
      'meta.tags': 'Tags',
      'meta.vendor_remarks': 'Vendor Remarks',
      'meta.draft_reason': 'Draft Reason',

      // Legacy meta fields
      'inventory': 'Inventory',
      'tags': 'Tags',
      'vendor_remarks': 'Vendor Remarks',

      // Other fields
      'variant_changes': 'Variant Changes'
    };

    // Handle meta.* pattern
    if (field && field.startsWith && field.startsWith('meta.')) {
      const metaField = field.substring(5); // Remove 'meta.' prefix
      return fieldMap[field] || fieldMap[metaField] || metaField.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }

    if (!field) return '';
    return fieldMap[field] || field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatValue = (value, field = null) => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';

    // Special handling for category_id - look up category name
    if (field === 'category_id' || field === 'category' || field === 'Category Id') {
      // If value is already an object with name, use it
      if (typeof value === 'object' && value !== null && value.name) {
        return value.name;
      }
      // Otherwise look up by ID or slug
      const category = categories.find(cat => cat.id === value || cat.slug === value);
      return category ? category.name : value;
    }

    if (typeof value === 'object') {
      // Special handling for category object
      if (value !== null && value.name && value.slug) {
        return value.name;
      }
      // For arrays, show as comma-separated list
      if (Array.isArray(value)) {
        return value.length > 0 ? value.join(', ') : '(empty)';
      }
      // For objects, show abbreviated form
      const keys = Object.keys(value);
      if (keys.length === 0) return '(empty)';
      if (keys.length <= 3) {
        return Object.entries(value).map(([k, v]) => `${k}: ${v}`).join(', ');
      }
      return `{${keys.length} fields}`;
    }
    return String(value);
  };



  const renderAuditHistorySection = () => {
    const toggleEntry = (index) => {
      setExpandedAuditEntries(prev => ({
        ...prev,
        [index]: !prev[index]
      }));
    };

    const formatFieldName = (field) => {
      // Convert field names to readable format
      return field
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .replace(/\./g, ' ')
        .trim()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    };

    const renderChangeDetails = (entry) => {
      const action = entry.action?.toLowerCase();

      // Don't show details for bulk_upload and approved
      if (action === 'bulk_upload' || action === 'approved') {
        return null;
      }

      // For status_change action, show formatted status transition
      if (action === 'status_change') {
        const statusChange = entry.status_change || entry.changes?.status || entry.changes?.status_change;
        if (statusChange && typeof statusChange === 'object') {
          const fromStatus = statusChange.from || statusChange.old || '—';
          const toStatus = statusChange.to || statusChange.new || '—';
          return (
            <div className="changesList">
              <h5>Status Change:</h5>
              <div className="statusChangeDetail">
                <span className="oldValue">{fromStatus}</span>
                <span className="arrow">→</span>
                <span className="newValue">{toStatus}</span>
              </div>
            </div>
          );
        }
        return null;
      }

      // For delete action, show reason
      if (action === 'delete') {
        const reason = entry.changes?.reason || entry.payload?.reason || entry.reason || 'No reason provided';
        const deleteReason = entry.payload?.deleted?.meta?.delete_reason || reason;
        return (
          <div className="changesList">
            <h5>Deletion Details:</h5>
            <div className="deleteReason">
              <strong>Reason:</strong>
              <p>{deleteReason}</p>
            </div>
          </div>
        );
      }



      // For update action, show all changes
      if (action === 'update' || action === 'product_update') {
        // Drop workflow-only transitions (state/terminal_status) before deciding
        // whether this entry has anything to show.
        const contentChanges = Object.entries(entry.changes || {}).filter(
          ([field]) => !NON_CONTENT_CHANGE_FIELDS.has(field)
        );

        if (contentChanges.length === 0) {
          return <p className="noChanges">No specific changes recorded</p>;
        }

        return (
          <div className="changesList">
            <h5>Changes Made:</h5>
            <ul>
              {contentChanges.map(([field, change]) => {
                // Handle variant_changes - can be array or object with changes array
                if (field === 'variant_changes') {
                  const changesArray = Array.isArray(change) ? change : (change?.changes || []);
                  
                  if (changesArray.length === 0) {
                    return null;
                  }
                  
                  return changesArray.map((variantChange, idx) => {
                    // Handle new variant added - simplified display
                    if (variantChange.action === 'added' && variantChange.field === 'new_variant') {
                      return (
                        <li key={`${field}-${idx}`} className="changeItem variant-change added">
                          new variant added : {variantChange.variant_name || variantChange.sku}
                        </li>
                      );
                    }
                    
                    // Handle variant deletion - simplified display
                    if (variantChange.action === 'deleted') {
                      return (
                        <li key={`${field}-${idx}`} className="changeItem variant-change removed">
                          variant removed : {variantChange.variant_name || variantChange.sku}
                        </li>
                      );
                    }
                    
                    // Handle regular variant field changes
                    return (
                      <li key={`${field}-${idx}`} className="changeItem variant-change">
                        <span className="fieldName">
                          {formatFieldName(variantChange.field)} (SKU: {variantChange.sku}):
                        </span>
                        <span className="changeValue">
                          <span className="oldValue">{variantChange.old === null || variantChange.old === '' ? '—' : variantChange.old}</span>
                          <span className="arrow">→</span>
                          <span className="newValue">{variantChange.new === null || variantChange.new === '' ? '—' : variantChange.new}</span>
                        </span>
                      </li>
                    );
                  });
                }

                // Handle action-based changes (media, meta with action property)
                if (typeof change === 'object' && change.action) {
                  // Handle media changes with detailed tracking
                  if (field.startsWith('media.')) {
                    const parts = field.split('.');
                    const action = parts[1]; // 'add', 'remove', or 'update'

                    if (action === 'add') {
                      return (
                        <li key={field} className="changeItem added">
                          <span className="fieldName">New image added:</span>
                          <span className="changeValue">
                            <span className="newValue">
                              {change.url && (
                                <a href={change.url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                                  {change.url.length > 60 ? change.url.substring(0, 60) + '...' : change.url}
                                </a>
                              )}
                              {change.alt_text && <span style={{ marginLeft: '0.5rem', fontStyle: 'italic' }}>({change.alt_text})</span>}
                            </span>
                          </span>
                        </li>
                      );
                    } else if (action === 'remove') {
                      return (
                        <li key={field} className="changeItem removed">
                          <span className="fieldName">Image removed:</span>
                          <span className="changeValue">
                            <span className="oldValue">
                              {change.url && (
                                <a href={change.url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                                  {change.url.length > 60 ? change.url.substring(0, 60) + '...' : change.url}
                                </a>
                              )}
                              {change.alt_text && <span style={{ marginLeft: '0.5rem', fontStyle: 'italic' }}>({change.alt_text})</span>}
                            </span>
                          </span>
                        </li>
                      );
                    } else if (action === 'update') {
                      const subAction = parts[2]; // 'url', 'alt', or undefined for both

                      // Handle URL and alt text both changed
                      if (!subAction && change.old_url && change.new_url) {
                        return (
                          <li key={field} className="changeItem">
                            <span className="fieldName">Image URL and alt text updated:</span>
                            <span className="changeValue">
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                <div>
                                  <strong>URL:</strong>
                                  <span className="oldValue" style={{ marginLeft: '0.5rem' }}>
                                    <a href={change.old_url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                                      {change.old_url.length > 40 ? change.old_url.substring(0, 40) + '...' : change.old_url}
                                    </a>
                                  </span>
                                  <span className="arrow">→</span>
                                  <span className="newValue">
                                    <a href={change.new_url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                                      {change.new_url.length > 40 ? change.new_url.substring(0, 40) + '...' : change.new_url}
                                    </a>
                                  </span>
                                </div>
                                {(change.old_alt || change.new_alt) && (
                                  <div>
                                    <strong>Alt:</strong>
                                    <span className="oldValue" style={{ marginLeft: '0.5rem' }}>{change.old_alt || '(none)'}</span>
                                    <span className="arrow">→</span>
                                    <span className="newValue">{change.new_alt || '(none)'}</span>
                                  </div>
                                )}
                              </div>
                            </span>
                          </li>
                        );
                      }

                      // Handle URL only changed
                      if (subAction === 'url' && change.old_url && change.new_url) {
                        return (
                          <li key={field} className="changeItem">
                            <span className="fieldName">Image URL updated:</span>
                            <span className="changeValue">
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                <div>
                                  <span className="oldValue">
                                    <a href={change.old_url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                                      {change.old_url.length > 50 ? change.old_url.substring(0, 50) + '...' : change.old_url}
                                    </a>
                                  </span>
                                </div>
                                <span className="arrow" style={{ margin: '0.25rem 0' }}>↓</span>
                                <div>
                                  <span className="newValue">
                                    <a href={change.new_url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                                      {change.new_url.length > 50 ? change.new_url.substring(0, 50) + '...' : change.new_url}
                                    </a>
                                  </span>
                                </div>
                                {change.alt_text && <span style={{ marginTop: '0.25rem', fontStyle: 'italic', fontSize: '0.85rem', color: '#666' }}>({change.alt_text})</span>}
                              </div>
                            </span>
                          </li>
                        );
                      }

                      // Handle alt text only changed (subAction === 'alt' or legacy format)
                      // Only show if there's an actual change (not both empty)
                      if (!change.old_alt && !change.new_alt) {
                        return null; // Don't show empty alt text changes
                      }
                      return (
                        <li key={field} className="changeItem">
                          <span className="fieldName">Image alt text updated:</span>
                          <span className="changeValue">
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                              {change.url && (
                                <a href={change.url} target="_blank" rel="noopener noreferrer" style={{ color: '#666', fontSize: '0.85rem', textDecoration: 'underline' }}>
                                  {change.url.length > 60 ? change.url.substring(0, 60) + '...' : change.url}
                                </a>
                              )}
                              <div>
                                <span className="oldValue">{change.old_alt || '(no alt text)'}</span>
                                <span className="arrow">→</span>
                                <span className="newValue">{change.new_alt || '(no alt text)'}</span>
                              </div>
                            </div>
                          </span>
                        </li>
                      );
                    }
                  } else if (field === 'media') {
                    // Legacy media change format (fallback)
                    return (
                      <li key={field} className="changeItem">
                        <span className="fieldName">Media:</span>
                        <span className="changeValue">{change.message || change.action}</span>
                      </li>
                    );
                  } else if (field.startsWith('meta.')) {
                    const metaKey = field.substring(5);

                    // Special handling for tags - skip displaying as generic meta field
                    if (metaKey === 'tags') {
                      if (change.action === 'updated') {
                        const oldTags = Array.isArray(change.old) ? change.old : [];
                        const newTags = Array.isArray(change.new) ? change.new : [];
                        const addedTags = newTags.filter(tag => !oldTags.includes(tag));
                        const removedTags = oldTags.filter(tag => !newTags.includes(tag));

                        return (
                          <React.Fragment key={field}>
                            {addedTags.map((tag, idx) => (
                              <li key={`${field}-added-${idx}`} className="changeItem added tag-change">
                                <span className="fieldName">New tag added:</span>
                                <span className="changeValue">
                                  <span className="newValue">{tag}</span>
                                </span>
                              </li>
                            ))}
                            {removedTags.map((tag, idx) => (
                              <li key={`${field}-removed-${idx}`} className="changeItem removed tag-change">
                                <span className="fieldName">Tag removed:</span>
                                <span className="changeValue">
                                  <span className="oldValue">{tag}</span>
                                </span>
                              </li>
                            ))}
                          </React.Fragment>
                        );
                      }
                      // Skip rendering tags for 'added' or 'removed' actions - they're handled above
                      return null;
                    }

                    if (change.action === 'added') {
                      return (
                        <li key={field} className="changeItem added">
                          <span className="fieldName">New meta field added:</span>
                          <span className="changeValue">
                            <span className="newValue">{metaKey}: {change.value}</span>
                          </span>
                        </li>
                      );
                    } else if (change.action === 'removed') {
                      return (
                        <li key={field} className="changeItem removed">
                          <span className="fieldName">Removed meta field:</span>
                          <span className="changeValue">
                            <span className="oldValue">{metaKey}: {change.old_value}</span>
                          </span>
                        </li>
                      );
                    } else if (change.action === 'updated') {
                      return (
                        <li key={field} className="changeItem">
                          <span className="fieldName">Meta field updated:</span>
                          <span className="changeValue">
                            <span className="oldValue">{metaKey}: {change.old}</span>
                            <span className="arrow">→</span>
                            <span className="newValue">{metaKey}: {change.new}</span>
                          </span>
                        </li>
                      );
                    }
                    // Meta field handled, don't fall through to regular field handling
                    return null;
                  }
                }

                // Regular field change with old/new values
                if (typeof change === 'object' && (change.old !== undefined || change.new !== undefined)) {
                  // Hide no-op rows such as "Options: (empty) → (empty)".
                  if (!isMeaningfulChange(change.old, change.new)) {
                    return null;
                  }
                  return (
                    <li key={field} className="changeItem">
                      <span className="fieldName">{formatFieldName(field)}:</span>
                      <span className="changeValue">
                        <span className="oldValue">{formatValue(change.old, field)}</span>
                        <span className="arrow">→</span>
                        <span className="newValue">{formatValue(change.new, field)}</span>
                      </span>
                    </li>
                  );
                }

                // Legacy format with from/to
                if (typeof change === 'object' && (change.from !== undefined || change.to !== undefined)) {
                  return (

                    <li key={field} className="changeItem">
                      <span className="fieldName">{formatFieldName(field)}:</span>
                      <span className="changeValue">
                        <span className="oldValue">{formatValue(change.from, field)}</span>
                        <span className="arrow">→</span>
                        <span className="newValue">{formatValue(change.to, field)}</span>
                      </span>
                    </li>
                  );
                }

                // Fallback for simple values
                return (
                  <li key={field} className="changeItem">
                    <span className="fieldName">{formatFieldName(field)}:</span>
                    <span className="changeValue">{JSON.stringify(change)}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      }

      // For other actions, show changes if available
      if (entry.changes && Object.keys(entry.changes).length > 0) {
        return (
          <div className="changesList">
            <h5>Details:</h5>
            <ul>
              {Object.entries(entry.changes).map(([field, change]) => (
                <li key={field} className="changeItem">
                  <span className="fieldName">{formatFieldName(field)}:</span>
                  <span className="changeValue">{JSON.stringify(change)}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      }

      return null;
    };

    return (
    <div className="shopifyCard">
      <div className="shopifyCardHeader">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Audit & History</h3>
            {isAdmin && (
              <button
                type="button"
                className="shopifyButton shopifyButtonSecondary"
                onClick={handleExportLogs}
                disabled={exportingLogs}
                style={{ padding: '8px 16px', fontSize: '14px' }}
              >
                {exportingLogs ? 'Exporting...' : '📥 Export Audit Logs'}
              </button>
            )}
        </div>
      </div>
      <div className="formSection">
        <div className="auditHistory">
            {auditHistory.length === 0 ? (
              <p className="noHistory">No audit history available</p>
            ) : (
              <div className="auditTimeline">
                {auditHistory.map((entry, index) => {
                  const isExpanded = expandedAuditEntries[index];
                  const action = entry.action?.toLowerCase();
                  const hasDetails = action === 'update' || action === 'product_update' || action === 'request_changes' || action === 'reject' || action === 'delete' || action === 'status_change';

                  return (
                    <div key={index} className={`auditEntry ${isExpanded ? 'expanded' : ''}`}>
                      <div
                        className="auditEntryHeader clickable"
                        onClick={() => toggleEntry(index)}
                      >
                        <div className="headerLeft">
                          <span className={`auditAction action-${action}`}>
                            {entry.action?.replace(/_/g, ' ').toUpperCase()}
                          </span>
                          {hasDetails && (
                            <span className="expandIcon">{isExpanded ? '▼' : '▶'}</span>
                          )}
                        </div>
                        <div className="headerRight">
                          <span className="auditDate">
                            {new Date(entry.timestamp).toLocaleString()}
                          </span>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="auditEntryDetails">
                          <div className="detailsGrid">
                            <div className="detailItem">
                              <span className="detailLabel">User:</span>
                              <span className="detailValue">{entry.changed_by?.name || entry.user || 'Unknown'}</span>
                            </div>
                            <div className="detailItem">
                              <span className="detailLabel">Role:</span>
                              <span className="detailValue">{entry.changed_by?.role || entry.user_role || 'Unknown'}</span>
                            </div>
                            <div className="detailItem">
                              <span className="detailLabel">Time:</span>
                              <span className="detailValue">
                                {new Date(entry.timestamp).toLocaleString('en-US', {
                                  dateStyle: 'medium',
                                  timeStyle: 'short'
                                })}
                              </span>
                            </div>
                          </div>

                          {entry.reason && (
                            <div className="reasonSection">
                              <strong>{(entry.action === 'request_changes') ? 'Changes requested' : (entry.action === 'reject' ? 'Rejection reason' : 'Reason')}:</strong>
                              <p>{entry.reason}</p>
                            </div>
                          )}

                          {renderChangeDetails(entry)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="approvalEditorView">
      <div className="approvalEditorHeader">
        <div className="headerLeft">
          <h2>Review Product: {product.title || 'Loading…'}</h2>
          <span className="productId">ID: {product.id || '—'}</span>
          {missingFields.length > 0 && (
            <span className="missingFieldsBadge">
              {missingFields.length} missing field(s)
            </span>
          )}
          {hasUnsavedChanges && (
            <span className="unsavedChangesBadge">Unsaved changes</span>
          )}
        </div>
        <div className="headerRight">
          <button
            onClick={onClose}
            className="closeButton"
          >
            ×
          </button>
        </div>
      </div>

      {/* Scrollable Content Area (notifications + content all scroll together) */}
      <div className="approvalEditorScrollable">
        <div className="approvalEditorContent" style={{ padding: '1.5rem' }}>
          {/* Loading Banner */}
          {productLoading && (
            <div className="infoBanner">
              Fetching latest product details…
            </div>
          )}

          {/* Vendor changes — structured snapshot-vs-current diff.
              Replaces the old flat <ul>, which recovered image URLs by
              string-splitting field names and had no variant or media sections. */}
          {!productLoading && (
            <ChangesReviewPanel
              changes={changes}
              legacyChanges={updatedFields}
              editors={editors}
            />
          )}

          {/* Missing Fields Alert */}
          {!productLoading && missingFields.length > 0 && (
            <div className="missingFieldsAlert">
              <h4>⚠️ Missing Required Fields</h4>
              <ul>
                {missingFields.map((field, index) => (
                  <li key={index}>
                    <strong>{formatFieldName(field.field)}:</strong> {field.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* All sections in shopifyLayout structure */}
          <div className="shopifyLayout">
            <div className="mainColumn">
              {renderOverviewSection()}
              {renderMediaSection()}
              {renderInventoryPricingSection()}
              {renderShippingSection()}
              {renderAuditHistorySection()}
            </div>
            <div className="sideColumn">
              <div className="shopifyCard">
                <div className="shopifyCardHeader"><h3>Product Status</h3></div>
                <StatusDropdown
                  currentStatus={formData.terminal_status}
                  onSelect={(value) => handleFieldChange('terminal_status', value)}
                />
                {formData.terminal_status !== product.terminal_status && (
                  <p className="statusDescription statusActionHint">
                    Save to submit this status change.
                  </p>
                )}
              </div>
              {renderVendorRemarksSection()}
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="approvalEditorFooter">
        <div className="approvalNotes">
          <label htmlFor="approvalNotes">Approval Notes (Optional)</label>
          <textarea
            id="approvalNotes"
            value={approvalNotes}
            onChange={(e) => setApprovalNotes(e.target.value)}
            placeholder="Add any notes about your review..."
            rows="3"
          />
        </div>

        <div className="approvalActions">
          <button
            onClick={onClose}
            className="shopifyButtonSecondary"
            style={{ marginRight: '0.5rem' }}
          >
            Cancel
          </button>
          {hasUnsavedChanges && (
            <button
              onClick={handleSaveEdit}
              className="approvalButton success"
            >
              💾 Save Changes
            </button>
          )}
          <button
            onClick={() => handleApprove('push')}
            className="approvalButton primary"
            disabled={approvingProduct}
          >
            {approvingProduct ? '⏳ Approving…' : 'Approve'}
          </button>
          <button
            onClick={handleRequestChanges}
            className="approvalButton warning"
          >
            Request Changes
          </button>
          <button
            onClick={handleReject}
            className="approvalButton danger"
          >
            Reject
          </button>
        </div>
      </div>

      {/* Reason Modal */}
      {showReasonModal && (
        <div className="modalOverlay">
          <div className="modalContent">
            <div className="modalHeader">
              <h3>
                {reasonModalAction === 'reject' ? 'Reject Product' : 'Request Changes'}
              </h3>
              <button
                className="modalClose"
                onClick={handleCloseReasonModal}
              >
                ×
              </button>
            </div>
            <div className="modalBody">
              <p>
                {reasonModalAction === 'reject'
                  ? 'Please provide a reason for rejecting this product.'
                  : 'Please describe the changes needed for this product.'
                }
              </p>
              <div className="formGroup">
                <label htmlFor="reason">Reason *</label>
                <textarea
                  id="reason"
                  placeholder={
                    reasonModalAction === 'reject'
                      ? 'Explain why this product is being rejected...'
                      : 'Describe the changes needed...'
                  }
                  rows="4"
                  required
                />
              </div>
            </div>
            <div className="modalFooter">
              <button
                className="modalButton secondary"
                onClick={handleCloseReasonModal}
              >
                Cancel
              </button>
              <button
                className={`modalButton ${reasonModalAction === 'reject' ? 'danger' : 'primary'}`}
                onClick={() => {
                  const reason = document.getElementById('reason').value;
                  if (reason.trim()) {
                    handleReasonSubmit(reason);
                  } else {
                    alert('Please provide a reason.');
                  }
                }}
              >
                {reasonModalAction === 'reject' ? 'Reject Product' : 'Request Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Edit Reason Modal */}
      {showAdminEditReasonModal && (
        <div className="modalOverlay">
          <div className="modalContent">
            <div className="modalHeader">
              <h3>Admin Edit - Provide Reason</h3>
              <button
                className="modalClose"
                onClick={cancelAdminEdit}
              >
                ×
              </button>
            </div>
            <div className="modalBody">
              <p>
                As an admin, you must provide a reason for editing this vendor's product.
                This will be logged in the audit history.
              </p>
              <div className="formGroup">
                <label htmlFor="adminEditReason">Reason for Edit *</label>
                <textarea
                  id="adminEditReason"
                  value={adminEditReason}
                  onChange={(e) => setAdminEditReason(e.target.value)}
                  placeholder="Explain why you are editing this product..."
                  rows="4"
                  required
                />
              </div>
            </div>
            <div className="modalFooter">
              <button
                className="modalButton secondary"
                onClick={cancelAdminEdit}
              >
                Cancel
              </button>
              <button
                className="modalButton primary"
                onClick={confirmAdminEdit}
                disabled={!adminEditReason || !adminEditReason.trim()}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ApprovalEditorView;
